// src/pages/dashboard/USDWalletDetail.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { getServerUrl } from '../../config/server.config';
import { zenusService } from '../../services/api/zenus.service';
import { getZenusAccount } from '../../store/assetsReducer';
import { AppDispatch } from '../../store';
import {
  ArrowLeftIcon,
  CopyIcon,
  DollarSign,
  Building,
  Send,
  Download,
  RefreshCw,
  Info,
  Check,
  Clock,
  AlertCircle,
  ArrowUpRight,
  ArrowDownLeft,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Trash2,
  Edit,
  Users,
  Zap,
  Globe,
  Repeat
} from 'lucide-react';
import { useWindow } from '../../context/WindowContext';
import '../../assets/styles/wallet-detail-button.css';

// Helper para obtener el estilo y label del tipo de pago
const getPaymentTypeBadge = (paymentType?: string) => {
  if (!paymentType) return null;

  const badges: Record<string, { icon: any; label: string; className: string }> = {
    'ACH': {
      icon: Building,
      label: 'ACH',
      className: 'bg-blue-100 text-blue-800'
    },
    'LOCAL_US_WIRE': {
      icon: Zap,
      label: 'FedWire',
      className: 'bg-purple-100 text-purple-800'
    },
    'SWIFT': {
      icon: Globe,
      label: 'SWIFT',
      className: 'bg-indigo-100 text-indigo-800'
    },
    'SWIFT_CROSSBORDER': {
      icon: Globe,
      label: 'SWIFT',
      className: 'bg-indigo-100 text-indigo-800'
    },
    'INTERNAL_TRANSFER': {
      icon: Repeat,
      label: 'Internal',
      className: 'bg-green-100 text-green-800'
    },
    'ZENUS_TRANSFER': {
      icon: Repeat,
      label: 'Internal',
      className: 'bg-green-100 text-green-800'
    }
  };

  return badges[paymentType] || { icon: Send, label: paymentType, className: 'bg-gray-100 text-gray-800' };
};

// Helper para obtener el estilo según el estado
const getStatusBadgeClass = (status: string) => {
  const statusMap: Record<string, string> = {
    'COMPLETED': 'bg-green-100 text-green-800',
    'PROCESSED': 'bg-green-100 text-green-800',
    'CONSUMED': 'bg-green-100 text-green-800',
    'PENDING': 'bg-yellow-100 text-yellow-800',
    'INSERTED': 'bg-yellow-100 text-yellow-800',
    'BLOCKED': 'bg-yellow-100 text-yellow-800',
    'PROCESSING': 'bg-blue-100 text-blue-800',
    'SUBMITTED': 'bg-blue-100 text-blue-800',
    'WAITING_FOR_AML': 'bg-orange-100 text-orange-800',
    'AWAITING_APPROVAL': 'bg-orange-100 text-orange-800',
    'FAILED': 'bg-red-100 text-red-800',
    'REJECTED': 'bg-red-100 text-red-800',
    'CANCELLED': 'bg-gray-100 text-gray-800',
    'REVERSED': 'bg-gray-100 text-gray-800',
    'RELEASED': 'bg-gray-100 text-gray-800',
    'UNKNOWN': 'bg-purple-100 text-purple-800'
  };

  return statusMap[status] || 'bg-gray-100 text-gray-800';
};

// Helper para obtener descripción informativa del estado
const getStatusDescription = (status: string): string => {
  const descriptions: Record<string, string> = {
    'COMPLETED': 'Transaction completed successfully',
    'PROCESSED': 'Transaction processed and will be settled soon',
    'CONSUMED': 'Fee applied to transaction',
    'PENDING': 'Transaction is pending processing',
    'INSERTED': 'Transaction created, awaiting processing',
    'BLOCKED': 'Funds reserved for this transaction',
    'PROCESSING': 'Transaction is being processed',
    'SUBMITTED': 'Transaction submitted to the network',
    'WAITING_FOR_AML': 'Transaction under compliance review',
    'AWAITING_APPROVAL': 'Transaction awaiting approval',
    'FAILED': 'Transaction failed',
    'REJECTED': 'Transaction was rejected',
    'CANCELLED': 'Transaction was cancelled',
    'REVERSED': 'Transaction was reversed/refunded',
    'RELEASED': 'Reserved funds were released',
    'UNKNOWN': 'Unknown status'
  };

  return descriptions[status] || 'Transaction status';
};

// Helper para obtener nombre amigable del estado
const getStatusDisplayName = (status: string): string => {
  const displayNames: Record<string, string> = {
    'COMPLETED': 'Completed',
    'PROCESSED': 'Processed',
    'CONSUMED': 'Applied',
    'PENDING': 'Pending',
    'INSERTED': 'Initiated',
    'BLOCKED': 'Reserved',
    'PROCESSING': 'Processing',
    'SUBMITTED': 'Sent',
    'WAITING_FOR_AML': 'In Review',
    'AWAITING_APPROVAL': 'Awaiting Approval',
    'FAILED': 'Failed',
    'REJECTED': 'Rejected',
    'CANCELLED': 'Cancelled',
    'REVERSED': 'Reversed',
    'RELEASED': 'Released',
    'UNKNOWN': 'Unknown'
  };

  return displayNames[status] || status;
};

interface ZenusAccount {
  id: string;
  zenusPersonId?: string;
  zenusAccountId?: string;
  accountNumber?: string;
  routingNumber?: string;
  swift?: string;
  iban?: string;
  accountStatus: string;
  accountName?: string;
  personName?: string;
  fullNameFromOnboarding?: string;
  bankLocation?: string;
  isLive?: boolean;
  lastSyncedAt?: string;
  balance?: {
    total: number;
    available: number;
    blocked: number;
    pending: number;
    currency: string;
    error?: string;
  };
  user?: {
    email: string;
    profile?: {
      firstName: string;
      lastName: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  };
}

interface Transaction {
  id: string;
  type: 'credit' | 'debit';
  amount: number;
  currency: string;
  description: string;
  status: string;
  createdAt: string;
  reference?: string;
  counterparty?: string;
  // Nuevos campos para mejoras visuales
  paymentType?: string; // 'ACH', 'SWIFT', 'LOCAL_US_WIRE', 'INTERNAL_TRANSFER', 'ZENUS_TRANSFER'
  direction?: 'IN' | 'OUT';
  fee?: number;
  // Campos para desglose de fees
  feeType?: string; // 'ZENUS_FEE', 'SERVICE_FEE'
  parentTransactionId?: string;
  netAmount?: number;
}

interface TransferFormData {
  recipientAccount: string;
  recipientName: string;
  recipientEmail?: string;
  amount: string;
  reference: string;
  transferType: 'internal' | 'ach' | 'wire' | 'fedwire';
  recipientBank?: string;
  routingNumber?: string;
  swiftCode?: string;
  accountNumberType?: 'BBAN' | 'IBAN';
  // ACH & SWIFT shared fields
  personType?: 'P' | 'L'; // P = Individual, L = Corporate/Legal
  firstName?: string; // For person type P
  lastName?: string; // For person type P
  beneficiaryAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  // ACH specific fields
  accountType?: 'CHECKING' | 'SAVINGS';
  identificationType?: 'PASSPORT' | 'DRIVER_LICENCE' | 'ID_CARD' | 'NATIONAL_IDENTIFICATION_NUMBER' | 'RESIDENCE_PERMIT';
  identificationValue?: string;
  bankAddress?: string;
  bankCity?: string;
  bankCountry?: string;
  purposeCode?: string;
  purposeMemo?: string;
  // SWIFT specific fields
  intermediaryBank?: string;
  intermediarySwift?: string;
  intermediaryBankCountry?: string;
}

interface BelobabaUser {
  email: string;
  name: string;
  zenusAccountId: string;
  accountNumber: string;
}

interface DepositInstructions {
  beneficiary: {
    name: string;
    accountNumber?: string;
    address?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  account: {
    accountNumber: string;
    routingNumber?: string;
    swiftCode?: string;
    iban?: string;
  };
  bank: {
    name: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  // VRN (Virtual Routing Number) information
  vrn?: {
    hasVRN: boolean;
    vrnNumber: string | null;
    vrnStatus: string | null;
  };
  // ACH Transfer specific data
  ach?: {
    beneficiaryName: string;
    beneficiaryAddress: string;
    accountNumber: string;
    routingNumber: string;
    accountType: string;
    bankName: string;
    bankAddress: string;
    bankCity: string;
    bankState: string;
    bankCountry: string;
    reference?: string; // Required when no VRN
  };
  // FedWire Transfer specific data
  fedwire?: {
    beneficiaryName: string;
    beneficiaryAddress: string;
    accountNumber: string;
    routingNumber: string;
    bankName: string;
    bankAddress: string;
    bankCity: string;
    bankState: string;
    bankCountry: string;
    reference?: string; // Required when no VRN
  };
  // SWIFT Transfer specific data
  swift?: {
    beneficiaryName: string;
    beneficiaryAddress: string;
    accountNumber: string;
    beneficiaryBank: {
      name: string;
      swiftCode: string;
      address: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    };
    intermediaryBank?: {
      name: string;
      swiftCode: string;
      routingNumber: string;
      address: string;
      city: string;
      state: string;
      country: string;
    };
  };
  masterAccount?: {
    accountId: string;
    accountNumber?: string;
    routingNumber?: string;
  } | null;
  metadata?: {
    source: 'zenus_api' | 'database_cache';
    lastFetched: string;
    warning?: string;
    error?: string;
    isUSAddress?: boolean;
  };
}

export default function USDWalletDetail() {
  const navigate = useNavigate();
  const { windowWidth } = useWindow();
  const isMobile = windowWidth <= 425;

  // Estados
  const [account, setAccount] = useState<ZenusAccount | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'deposit' | 'transfer' | 'history' | 'usdc'>('overview');
  const [historyFilter, setHistoryFilter] = useState<'all' | 'incoming' | 'outgoing'>('all');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [transferForm, setTransferForm] = useState<TransferFormData>({
    recipientAccount: '',
    recipientName: '',
    recipientEmail: '',
    amount: '',
    reference: '',
    transferType: 'internal',
    accountType: 'CHECKING',
    beneficiaryAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    purposeCode: '1', // Personal / Living expenses
    personType: 'P',
    bankAddress: '',
    bankCity: '',
    bankCountry: ''
  });
  const [isProcessingTransfer, setIsProcessingTransfer] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<any>(null);
  const [newBalance, setNewBalance] = useState<number | null>(null);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [belobabaUsers, setBelobabaUsers] = useState<BelobabaUser[]>([]);
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);

  // Deposit instructions states
  const [depositInstructions, setDepositInstructions] = useState<DepositInstructions | null>(null);
  const [loadingDepositInstructions, setLoadingDepositInstructions] = useState(false);
  const [depositInstructionsError, setDepositInstructionsError] = useState<string | null>(null);

  // USDC Wallet states
  const [usdcWallet, setUsdcWallet] = useState<any>(null);
  const [loadingUsdcWallet, setLoadingUsdcWallet] = useState(false);
  const [creatingUsdcWallet, setCreatingUsdcWallet] = useState(false);

  const handleConfirmTransfer = async () => {
    if (!pendingPayment?.paymentId) return;

    setIsProcessingTransfer(true);
    setShowConfirmationModal(false);

    try {
      const isInternalTransfer = pendingPayment?.type === 'INTERNAL_TRANSFER';

      if (isInternalTransfer) {
        // Transferencia interna ya está completada, solo actualizar balance
        await dispatch(getZenusAccount());
        await loadAccountData(false);

        const recipientInfo = transferForm.recipientEmail || transferForm.recipientAccount;
        setSuccessMessage(`Internal transfer completed successfully!\n\nThe recipient (${recipientInfo}) now has access to the funds.`);
      } else {
        // Transferencia externa - confirmar con Zenus API
        const confirmResponse = await zenusService.confirmPayment(pendingPayment.paymentId);

        if (confirmResponse.success) {
          // Actualizar balance con el valor retornado por el backend
          if (confirmResponse.balance) {
            setNewBalance(confirmResponse.balance.total || confirmResponse.balance);
          }

          await dispatch(getZenusAccount());
          await loadAccountData(false);

          const transferType = transferForm.transferType === 'ach' ? 'ACH' :
                               transferForm.transferType === 'fedwire' ? 'FedWire' : 'SWIFT/Wire';
          setSuccessMessage(`Transfer completed successfully!\n\nAmount: $${transferForm.amount}\nType: ${transferType}\nRecipient: ${transferForm.recipientName}`);
        } else {
          toast.error('Failed to confirm transfer');
          return;
        }
      }

      // Limpiar formulario
      setTransferForm({
        recipientAccount: '',
        recipientName: '',
        recipientEmail: '',
        amount: '',
        reference: '',
        transferType: 'internal',
        accountType: 'CHECKING',
        beneficiaryAddress: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },
        purposeCode: '1',
        personType: 'P',
        bankAddress: '',
        bankCity: '',
        bankCountry: ''
      });

      // Mostrar modal de éxito
      setShowSuccessModal(true);
      setPendingPayment(null);

    } catch (error: any) {
      console.error('Error confirming transfer:', error);
      toast.error(error.message || 'Failed to confirm transfer');
    } finally {
      setIsProcessingTransfer(false);
    }
  };

  const handleCancelTransfer = () => {
    setShowConfirmationModal(false);
    setPendingPayment(null);
    setShowTransferModal(true);
  };

  // USDC Wallet Functions
  const loadUsdcWallet = async () => {
    try {
      setLoadingUsdcWallet(true);
      const response = await zenusService.getUSDCAddress();

      if (response.success) {
        setUsdcWallet(response);
      }
    } catch (error: any) {
      console.error('Error loading USDC wallet:', error);
    } finally {
      setLoadingUsdcWallet(false);
    }
  };

  const handleCreateUsdcWallet = async () => {
    try {
      setCreatingUsdcWallet(true);
      const response = await zenusService.createUSDCWallet();

      if (response.success) {
        toast.success('USDC wallet created successfully!');
        await loadUsdcWallet();
      } else {
        // Si requiere activación manual, mostrar mensaje específico
        if (response.requiresManualActivation) {
          toast.error(
            response.message ||
            'Your Zenus account needs USDC activation. Please contact support.'
          );
        } else {
          toast.error(response.message || response.error || 'Failed to create USDC wallet');
        }
      }
    } catch (error: any) {
      console.error('Error creating USDC wallet:', error);

      // Mostrar mensaje del servidor si está disponible
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'Failed to create USDC wallet';

      toast.error(errorMessage);
    } finally {
      setCreatingUsdcWallet(false);
    }
  };

  // Counterparty management states
  const [savedCounterparties, setSavedCounterparties] = useState<any[]>([]);
  const [showSaveCounterpartyModal, setShowSaveCounterpartyModal] = useState(false);
  const [counterpartyNickname, setCounterpartyNickname] = useState('');
  const [showManageCounterparties, setShowManageCounterparties] = useState(false);
  const [counterpartyToDelete, setCounterpartyToDelete] = useState<any>(null);
  const [counterpartyToEdit, setCounterpartyToEdit] = useState<any>(null);
  const [showEditCounterpartyModal, setShowEditCounterpartyModal] = useState(false);

  // Función para calcular el fee según el tipo de transferencia
  const calculateTransferFee = (transferType: string): number => {
    switch (transferType) {
      case 'ach':
        return 15;
      case 'fedwire':
        return 25;
      case 'wire': // SWIFT
        return 25;
      case 'internal':
        return 0;
      default:
        return 0;
    }
  };

  // Helper: Obtener texto del purpose code
  const getPurposeText = (code: string): string => {
    const purposes: Record<string, string> = {
      '1': 'Personal / Living Expenses',
      '2': 'Credit Card Payment',
      '3': 'Personal Investment',
      '4': 'Loan Repayment',
      '5': 'Family Support',
      '6': 'Educational / Student Support',
      '7': 'Rent Payment',
      '8': 'Utility Bill Payment',
      '9': 'Charitable Donation',
      '10': 'Purchase of Personal Goods',
      '11': 'Gift',
      '12': 'Insurance Premium',
      '13': 'Medical Bills',
      '14': 'Purchase of Property',
      '15': 'Salary Transfer',
      '16': 'Preferable Saving',
      '17': 'Loan to Beneficiary',
      '18': 'Travel Expenses',
      '19': 'School / College Tuition Fees',
      '20': 'Mortgage Payment',
      '21': 'Household Repairs / Building Cost',
      '22': 'Closure of Remitting Account',
      '23': 'Hire of Goods',
      '24': 'Fines / Penalties',
      '25': 'Tax Bill',
      '26': 'Dividends',
      '27': 'Agricultural Goods',
      '28': 'Real Estate Investments',
      '29': 'Import/Export Settlements',
      '30': 'Art Purchase Settlements',
      '31': 'Other'
    };
    return purposes[code] || 'Personal / Living Expenses';
  };

  // Cargar datos de la cuenta
  const loadAccountData = async (showLoader = true) => {
    if (showLoader) setIsLoading(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const serverUrl = getServerUrl(false); // false para obtener la URL base sin /api

      // Obtener datos de la cuenta
      const accountResponse = await fetch(`${serverUrl}/api/providers/zenus/my-account`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (accountResponse.ok) {
        const accountData = await accountResponse.json();
        if (accountData.success && accountData.data.hasAccount) {
          console.log('Full account data received:', accountData.data);

          // Obtener balance en tiempo real desde el nuevo endpoint
          const balanceResponse = await fetch(`${serverUrl}/api/providers/zenus/balance`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          let balanceData = {
            total: 0,
            available: 0,
            pending: 0,
            currency: 'USD'
          };

          if (balanceResponse.ok) {
            const balanceResult = await balanceResponse.json();
            if (balanceResult.success && balanceResult.data) {
              balanceData = {
                total: balanceResult.data.total || 0,
                available: balanceResult.data.available || 0,
                pending: balanceResult.data.pending || 0,
                currency: balanceResult.data.currency || 'USD'
              };
              console.log('Real-time balance:', balanceData);
            }
          }

          const accountWithBalance = {
            ...accountData.data.account,
            balance: balanceData
          };

          console.log('Account with balance:', accountWithBalance);
          setAccount(accountWithBalance);
        } else {
          toast.error('No Zenus account found');
          navigate('/dashboard/wallets');
          return;
        }
      }

      // Obtener transacciones del cliente (no las master)
      const transactionsResponse = await fetch(`${serverUrl}/api/providers/zenus/transactions?pageSize=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (transactionsResponse.ok) {
        const transData = await transactionsResponse.json();
        console.log('Transactions response:', transData);
        if (transData.success && transData.data) {
          // Las transacciones vienen en data.transactions
          const zenusTransactions = transData.data.transactions || [];

          // Mapear las transacciones al formato esperado por el componente
          const mappedTransactions = zenusTransactions.map((tx: any) => {
            // Extraer el nombre de la contrapartida si es un objeto
            let counterpartyName = '';
            if (tx.counterparty) {
              if (typeof tx.counterparty === 'object') {
                counterpartyName = tx.counterparty.name || tx.counterparty.accountNumber || '';
              } else {
                counterpartyName = tx.counterparty;
              }
            } else if (tx.senderName) {
              counterpartyName = tx.senderName;
            } else if (tx.recipientName) {
              counterpartyName = tx.recipientName;
            }

            // Determinar si es credit o debit basándose PRIMERO en direction, luego en type
            const parsedAmount = parseFloat(tx.amount || 0);

            // PRIORIDAD 1: Si tiene direction, usarla (es la fuente de verdad)
            let isCredit: boolean;
            if (tx.direction) {
              isCredit = tx.direction === 'IN';
            } else {
              // PRIORIDAD 2: Si no tiene direction, usar type
              // DEBIT siempre es salida, CREDIT/DEPOSIT siempre es entrada
              // Solo usar el signo del amount como último recurso
              if (tx.type === 'DEBIT') {
                isCredit = false;
              } else if (tx.type === 'CREDIT' || tx.type === 'DEPOSIT') {
                isCredit = true;
              } else {
                // Último recurso: usar signo del amount
                isCredit = parsedAmount > 0;
              }
            }

            return {
              id: tx.id || tx.transactionId || Date.now().toString(),
              type: isCredit ? 'credit' : 'debit',
              amount: Math.abs(parsedAmount),
              currency: tx.currency || 'USD',
              // Usar displayName (nombre amigable) si viene del backend, si no usar description
              description: tx.displayName || tx.description || tx.reference || 'Transaction',
              status: tx.status || 'UNKNOWN',
              createdAt: tx.date || tx.transactionDate || tx.createdAt,
              reference: tx.reference,
              counterparty: counterpartyName,
              // Nuevos campos
              paymentType: tx.type || tx.paymentType,
              direction: tx.direction,
              fee: tx.fee ? parseFloat(tx.fee) : undefined,
              // Campos adicionales para desglose de fees
              feeType: tx.feeType,
              parentTransactionId: tx.parentTransactionId,
              netAmount: tx.netAmount ? parseFloat(tx.netAmount) : undefined
            };
          });

          setTransactions(mappedTransactions);
        } else {
          setTransactions([]);
        }
      }
    } catch (error) {
      console.error('Error loading account data:', error);
      toast.error('Failed to load account data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Load deposit instructions from Zenus API
  const loadDepositInstructions = async () => {
    setLoadingDepositInstructions(true);
    setDepositInstructionsError(null);

    try {
      const response = await zenusService.getDepositInstructions();

      if (response.success && response.data) {
        console.log('Deposit instructions loaded:', response.data);
        setDepositInstructions(response.data);

        // Show warning if data is from cache
        if (response.data.metadata?.source === 'database_cache') {
          toast.warning('Showing cached deposit information. Live data unavailable.');
        }
      } else {
        throw new Error('Failed to load deposit instructions');
      }
    } catch (error: any) {
      console.error('Error loading deposit instructions:', error);
      setDepositInstructionsError(error.message || 'Failed to load deposit instructions');
      toast.error('Could not load deposit instructions. Please try again.');
    } finally {
      setLoadingDepositInstructions(false);
    }
  };

  // Load saved counterparties
  const loadCounterparties = async () => {
    try {
      const transferType = transferForm.transferType === 'ach' ? 'ACH' :
                          transferForm.transferType === 'fedwire' ? 'FEDWIRE' :
                          transferForm.transferType === 'wire' ? 'SWIFT' : null;

      console.log('Loading counterparties for transferType:', transferType);

      if (transferType) {
        const response = await zenusService.getCounterparties(transferType);
        console.log('Counterparties response:', response);
        if (response.success) {
          setSavedCounterparties(response.data || []);
          console.log('Saved counterparties loaded:', response.data?.length || 0);
        } else {
          console.error('Failed to load counterparties:', response);
        }
      }
    } catch (error) {
      console.error('Error loading counterparties:', error);
    }
  };

  // Cargar datos al montar
  useEffect(() => {
    loadAccountData();
  }, []);

  // Load deposit instructions when deposit tab is activated
  useEffect(() => {
    if (activeTab === 'deposit' && !depositInstructions && !loadingDepositInstructions) {
      loadDepositInstructions();
    }
  }, [activeTab]);

  // Load USDC wallet when USDC tab is activated
  useEffect(() => {
    if (activeTab === 'usdc' && !usdcWallet && !loadingUsdcWallet) {
      loadUsdcWallet();
    }
  }, [activeTab]);

  // Load counterparties when transfer type changes
  useEffect(() => {
    if (transferForm.transferType === 'ach' || transferForm.transferType === 'fedwire' || transferForm.transferType === 'wire') {
      loadCounterparties();
    }
  }, [transferForm.transferType]);

  // Función para refrescar datos
  const handleRefresh = () => {
    setIsRefreshing(true);
    loadAccountData(false);
  };

  // Copiar al portapapeles
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Abrir modal de detalles de transacción
  const openTransactionDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  // Cerrar modal de detalles
  const closeTransactionDetails = () => {
    setShowTransactionModal(false);
    setTimeout(() => setSelectedTransaction(null), 300); // Delay para animación
  };

  // Buscar usuarios Belobaba
  const searchBelobabaUsers = async (email: string) => {
    if (!email || email.length < 3) {
      setBelobabaUsers([]);
      setShowUserSuggestions(false);
      return;
    }

    setSearchingUsers(true);
    try {
      const response = await zenusService.searchUsersByEmail(email);
      if (response.success) {
        setBelobabaUsers(response.data || []);
        setShowUserSuggestions(response.data.length > 0);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Seleccionar usuario Belobaba
  const selectBelobabaUser = (user: BelobabaUser) => {
    setTransferForm({
      ...transferForm,
      recipientEmail: user.email,
      recipientName: user.name,
      recipientAccount: user.accountNumber // Usar accountNumber real de Zenus
    });
    setShowUserSuggestions(false);
  };

  // Load counterparty data into form
  const loadCounterpartyIntoForm = (counterparty: any) => {
    setTransferForm({
      ...transferForm,
      recipientAccount: counterparty.accountNumber || '',
      recipientName: counterparty.beneficiaryName || '',
      firstName: counterparty.givenName || '',
      lastName: counterparty.surname || '',
      reference: counterparty.reference || '',
      recipientBank: counterparty.bankName || '',
      routingNumber: counterparty.routingNumber || '',
      swiftCode: counterparty.swiftCode || '',
      personType: counterparty.personType || 'P',
      beneficiaryAddress: {
        addressLine1: counterparty.addressStreet1 || '',
        addressLine2: counterparty.addressStreet2 || '',
        city: counterparty.addressCity || '',
        state: counterparty.addressState || '',
        zipCode: counterparty.addressZip || '',
        country: counterparty.addressCountry || ''
      },
      bankAddress: counterparty.bankAddress || '',
      bankCity: counterparty.bankCity || '',
      bankCountry: counterparty.bankCountry || '',
      intermediarySwift: counterparty.intermediaryBankBIC || '',
      intermediaryBankCountry: counterparty.intermediaryBankCountry || ''
    });
    toast.success(`Loaded: ${counterparty.nickname}`);
  };

  // Save current form as counterparty
  const handleSaveCounterparty = async () => {
    if (!counterpartyNickname.trim()) {
      toast.error('Please enter a nickname for this beneficiary');
      return;
    }

    try {
      const transferType: 'ACH' | 'SWIFT' | 'FEDWIRE' = transferForm.transferType === 'ach' ? 'ACH' :
                          transferForm.transferType === 'fedwire' ? 'FEDWIRE' :
                          transferForm.transferType === 'wire' ? 'SWIFT' : 'FEDWIRE';

      const data = {
        nickname: counterpartyNickname,
        transferType,
        beneficiaryName: transferForm.recipientName,
        personType: transferForm.personType || 'P',
        givenName: transferForm.firstName,
        surname: transferForm.lastName,
        accountNumber: transferForm.recipientAccount,
        accountCountryCode: transferForm.beneficiaryAddress?.country || 'US',
        routingNumber: transferForm.routingNumber,
        swiftCode: transferForm.swiftCode,
        addressStreet1: transferForm.beneficiaryAddress?.addressLine1 || '',
        addressStreet2: transferForm.beneficiaryAddress?.addressLine2,
        addressCity: transferForm.beneficiaryAddress?.city || '',
        addressState: transferForm.beneficiaryAddress?.state,
        addressZip: transferForm.beneficiaryAddress?.zipCode,
        addressCountry: transferForm.beneficiaryAddress?.country || 'US',
        bankName: transferForm.recipientBank,
        bankAddress: transferForm.bankAddress,
        bankCity: transferForm.bankCity,
        bankCountry: transferForm.bankCountry,
        intermediaryBankBIC: transferForm.intermediarySwift,
        intermediaryBankCountry: transferForm.intermediaryBankCountry,
        reference: transferForm.reference
      };

      await zenusService.createCounterparty(data);
      toast.success('Beneficiary saved successfully');
      setShowSaveCounterpartyModal(false);
      setCounterpartyNickname('');
      loadCounterparties(); // Reload list
    } catch (error: any) {
      console.error('Error saving counterparty:', error);
      toast.error(error.message || 'Failed to save beneficiary');
    }
  };

  // Delete a counterparty
  const handleDeleteCounterparty = async () => {
    if (!counterpartyToDelete) return;

    try {
      await zenusService.deleteCounterparty(counterpartyToDelete.id);
      toast.success('Beneficiary deleted successfully');
      setCounterpartyToDelete(null);
      loadCounterparties(); // Reload list
    } catch (error: any) {
      console.error('Error deleting counterparty:', error);
      toast.error(error.message || 'Failed to delete beneficiary');
    }
  };

  // Open edit modal with counterparty data
  const openEditCounterparty = (counterparty: any) => {
    setCounterpartyToEdit(counterparty);
    setCounterpartyNickname(counterparty.nickname);
    setShowEditCounterpartyModal(true);
  };

  // Update a counterparty
  const handleUpdateCounterparty = async () => {
    if (!counterpartyToEdit || !counterpartyNickname.trim()) {
      toast.error('Please enter a nickname for this beneficiary');
      return;
    }

    try {
      const data = {
        nickname: counterpartyNickname,
        beneficiaryName: counterpartyToEdit.beneficiaryName,
        personType: counterpartyToEdit.personType,
        accountNumber: counterpartyToEdit.accountNumber,
        accountNumberType: counterpartyToEdit.accountNumberType || 'BBAN',
        accountCountryCode: counterpartyToEdit.accountCountryCode,
        routingNumber: counterpartyToEdit.routingNumber,
        swiftCode: counterpartyToEdit.swiftCode,
        addressStreet1: counterpartyToEdit.addressStreet1,
        addressStreet2: counterpartyToEdit.addressStreet2,
        addressCity: counterpartyToEdit.addressCity,
        addressState: counterpartyToEdit.addressState,
        addressZip: counterpartyToEdit.addressZip,
        addressCountry: counterpartyToEdit.addressCountry,
        bankName: counterpartyToEdit.bankName,
        bankAddress: counterpartyToEdit.bankAddress,
        bankCountry: counterpartyToEdit.bankCountry,
        intermediaryBankBIC: counterpartyToEdit.intermediaryBankBIC
      };

      await zenusService.updateCounterparty(counterpartyToEdit.id, data);
      toast.success('Beneficiary updated successfully');
      setShowEditCounterpartyModal(false);
      setCounterpartyToEdit(null);
      setCounterpartyNickname('');
      loadCounterparties(); // Reload list
    } catch (error: any) {
      console.error('Error updating counterparty:', error);
      toast.error(error.message || 'Failed to update beneficiary');
    }
  };

  // Manejar confirmación del modal de éxito
  const handleSuccessConfirm = async () => {
    setShowSuccessModal(false);
    setNewBalance(null);

    // Redirigir a Overview
    navigate('/dashboard');
  };

  // Procesar transferencia
  const handleTransfer = async () => {
    if (!transferForm.amount || !transferForm.reference) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validar que el monto no exceda el balance disponible
    const transferAmount = parseFloat(transferForm.amount);
    if (isNaN(transferAmount) || transferAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Calcular el fee y el total requerido
    const transferFee = calculateTransferFee(transferForm.transferType);
    const totalRequired = transferAmount + transferFee;

    if (account.balance && totalRequired > account.balance.available) {
      toast.error(
        `Insufficient balance. You need $${totalRequired.toFixed(2)} (${transferAmount.toFixed(2)} + $${transferFee.toFixed(2)} fee), but only $${account.balance.available.toLocaleString()} is available.`
      );
      return;
    }

    // Validaciones según tipo de transferencia
    if (transferForm.transferType === 'internal') {
      if (!transferForm.recipientEmail && !transferForm.recipientAccount) {
        toast.error('Please provide recipient email or account number');
        return;
      }
    } else {
      if (!transferForm.recipientAccount || !transferForm.recipientName) {
        toast.error('Please provide recipient account number and name');
        return;
      }
    }

    // Validación adicional para ACH
    if (transferForm.transferType === 'ach') {
      if (!transferForm.recipientBank || !transferForm.routingNumber) {
        toast.error('Bank name and routing number are required for ACH transfers');
        return;
      }

      if (!transferForm.beneficiaryAddress?.addressLine1 ||
          !transferForm.beneficiaryAddress?.city ||
          !transferForm.beneficiaryAddress?.state ||
          !transferForm.beneficiaryAddress?.zipCode) {
        toast.error('Complete US address is required for ACH transfers');
        return;
      }

      // Validar que sea una dirección de EE.UU.
      if (transferForm.beneficiaryAddress.country !== 'US') {
        toast.error('ACH transfers are only available for US addresses. For international transfers, please use SWIFT/Wire option.');
        return;
      }

      // Validar formato de routing number (9 dígitos)
      if (!/^\d{9}$/.test(transferForm.routingNumber)) {
        toast.error('Routing number must be exactly 9 digits');
        return;
      }

      // Validar formato de estado (2 letras)
      if (!/^[A-Z]{2}$/.test(transferForm.beneficiaryAddress.state)) {
        toast.error('State must be a valid 2-letter US state code');
        return;
      }
    }

    // Validación para FedWire (similar a ACH)
    if (transferForm.transferType === 'fedwire') {
      if (!transferForm.recipientBank || !transferForm.routingNumber) {
        toast.error('Bank name and routing number are required for FedWire transfers');
        return;
      }

      if (!transferForm.beneficiaryAddress?.addressLine1 ||
          !transferForm.beneficiaryAddress?.city) {
        toast.error('Beneficiary address is required for FedWire transfers');
        return;
      }

      // Validar formato de routing number (9 dígitos)
      if (!/^\d{9}$/.test(transferForm.routingNumber)) {
        toast.error('Routing number must be exactly 9 digits');
        return;
      }
    }

    // Validación para SWIFT
    if (transferForm.transferType === 'wire') {
      if (!transferForm.recipientBank || !transferForm.swiftCode) {
        toast.error('Bank name and SWIFT code are required for wire transfers');
        return;
      }

      if (!transferForm.bankAddress || !transferForm.bankCity || !transferForm.bankCountry) {
        toast.error('Complete bank address is required for SWIFT transfers');
        return;
      }

      if (!transferForm.beneficiaryAddress?.addressLine1 ||
          !transferForm.beneficiaryAddress?.city ||
          !transferForm.beneficiaryAddress?.country) {
        toast.error('Complete beneficiary address is required for SWIFT transfers');
        return;
      }
    }

    setIsProcessingTransfer(true);

    try {
      let response;

      if (transferForm.transferType === 'internal') {
        // Transferencia interna
        response = await zenusService.initializeInternalTransfer({
          recipientEmail: transferForm.recipientEmail,
          recipientAccount: transferForm.recipientAccount,
          amount: parseFloat(transferForm.amount),
          reference: transferForm.reference,
          details: transferForm.reference
        });
      } else if (transferForm.transferType === 'ach') {
        // Transferencia ACH - mapeo correcto al body de Zenus
        const counterpartyData: any = {
          name: transferForm.recipientName,
          personType: transferForm.personType || 'P',
          accountNumber: transferForm.recipientAccount,
          routingNumber: transferForm.routingNumber!,
          address: {
            street1: transferForm.beneficiaryAddress!.addressLine1,
            city: transferForm.beneficiaryAddress!.city,
            state: transferForm.beneficiaryAddress!.state,
            zip: transferForm.beneficiaryAddress!.zipCode,
            country: 'US'
          },
          bankName: transferForm.recipientBank,
          bankAddress: transferForm.beneficiaryAddress!.city, // Ciudad del banco
          bankCountry: 'US'
        };

        // Solo agregar firstName/lastName si es Individual (P)
        if (transferForm.personType === 'P' && transferForm.firstName && transferForm.lastName) {
          counterpartyData.firstName = transferForm.firstName;
          counterpartyData.lastName = transferForm.lastName;
        }

        response = await zenusService.initializeACHTransfer({
          amount: parseFloat(transferForm.amount),
          details: transferForm.reference || `ACH to ${transferForm.recipientName}`,
          referenceNumber: transferForm.reference,
          purposeCode: transferForm.purposeCode || '1',
          purposeMemo: getPurposeText(transferForm.purposeCode || '1'),
          deviceChannel: 'Web',
          counterparty: counterpartyData
        });
      } else if (transferForm.transferType === 'fedwire') {
        // Transferencia FedWire - Similar a ACH pero same-day
        const counterpartyData: any = {
          name: transferForm.recipientName,
          personType: transferForm.personType || 'P',
          accountNumber: transferForm.recipientAccount,
          routingNumber: transferForm.routingNumber!,
          address: {
            street1: transferForm.beneficiaryAddress!.addressLine1,
            city: transferForm.beneficiaryAddress!.city,
            state: transferForm.beneficiaryAddress!.state,
            zip: transferForm.beneficiaryAddress!.zipCode,
            country: transferForm.beneficiaryAddress!.country || 'US'
          },
          bankName: transferForm.recipientBank,
          bankAddress: transferForm.beneficiaryAddress!.city,
          bankCountry: 'US'
        };

        // Solo agregar firstName/lastName si es Individual (P)
        if (transferForm.personType === 'P' && transferForm.firstName && transferForm.lastName) {
          counterpartyData.firstName = transferForm.firstName;
          counterpartyData.lastName = transferForm.lastName;
        }

        response = await zenusService.initializeFedWireTransfer({
          amount: parseFloat(transferForm.amount),
          details: transferForm.reference || `FedWire to ${transferForm.recipientName}`,
          referenceNumber: transferForm.reference,
          purposeCode: transferForm.purposeCode || '1',
          purposeMemo: getPurposeText(transferForm.purposeCode || '1'),
          deviceChannel: 'Web',
          counterparty: counterpartyData
        });
      } else {
        // Transferencia SWIFT
        const personType = transferForm.personType || 'P';
        const nameParts = transferForm.recipientName.split(' ');
        const swiftGivenName = nameParts[0] || '';
        const swiftSurname = nameParts.slice(1).join(' ') || nameParts[0];

        const counterpartyData: any = {
          name: transferForm.recipientName,
          personType: personType,
          accountNumber: transferForm.recipientAccount,
          accountNumberType: 'BBAN', // Siempre BBAN según receipt
          accountCountryCode: transferForm.beneficiaryAddress!.country, // País de la cuenta del beneficiario
          swiftCode: transferForm.swiftCode!,
          address: {
            street1: transferForm.beneficiaryAddress!.addressLine1,
            city: transferForm.beneficiaryAddress!.city,
            state: transferForm.beneficiaryAddress!.state,
            zip: transferForm.beneficiaryAddress!.zipCode,
            country: transferForm.beneficiaryAddress!.country
          },
          bankName: transferForm.recipientBank!,
          bankAddress: transferForm.bankAddress!,
          bankCountry: transferForm.bankCountry!,
          intermediaryBankBIC: transferForm.intermediarySwift,
          intermediaryBankCountry: transferForm.intermediaryBankCountry
        };

        // Agregar givenName y surname solo si es persona
        if (personType === 'P') {
          counterpartyData.givenName = swiftGivenName;
          counterpartyData.surname = swiftSurname;
        }

        response = await zenusService.initializeSWIFTTransfer({
          amount: parseFloat(transferForm.amount),
          details: transferForm.reference,
          // endToEndId se genera automáticamente en el backend
          referenceNumber: transferForm.reference,
          purposeCode: transferForm.purposeCode || '1',
          counterparty: counterpartyData
        });
      }

      if (response.success) {
        // Guardar el pago pendiente y mostrar modal de confirmación
        setPendingPayment(response.payment);
        setShowTransferModal(false);
        setShowConfirmationModal(true);

        // CRÍTICO: Actualizar balance inmediatamente después de crear la transferencia
        // Esto asegura que los fondos bloqueados se muestren al instante en todos los componentes
        const zenusData = await dispatch(getZenusAccount()).unwrap();
        await loadAccountData(false);

        // Mostrar el nuevo balance disponible en el modal de confirmación
        // El balance ya refleja los fondos bloqueados (monto de la transferencia)
        // El fee se cobrará cuando se COMPLETE la transferencia
        if (zenusData && zenusData.balance !== undefined) {
          setNewBalance(zenusData.balance);
        }
      } else {
        toast.error(response.error || 'Transfer failed');
      }
    } catch (error: any) {
      console.error('Transfer error:', error);
      toast.error(error.message || 'Failed to process transfer');
    } finally {
      setIsProcessingTransfer(false);
    }
  };

  // Estilos dinámicos
  const headerStyles = isMobile
    ? 'flex flex-wrap items-center justify-between mb-7'
    : 'flex items-center justify-between mb-[30px]';

  const titleStyles = isMobile
    ? 'w-full text-3xl ml-2 mb-3'
    : 'text-[32px] ml-[10px]';

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-[70vh]'>
        <div className='text-center'>
          <RefreshCw className='animate-spin mx-auto mb-4' size={40} color='#B3A369' />
          <p className='text-gray-600'>Loading account details...</p>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className='flex items-center justify-center h-[70vh]'>
        <div className='text-center'>
          <AlertCircle className='mx-auto mb-4' size={40} color='#dc2626' />
          <p className='text-gray-600'>No account found</p>
          <button
            className='wallet-btn wallet-btn-default mt-4'
            onClick={() => navigate('/dashboard/wallets')}
          >
            Back to Wallets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='sm:p-10 h-full'>
      {/* Header */}
      <div className={headerStyles}>
        <div className='flex items-center w-full'>
          {!isMobile && (
            <button
              className='wallet-btn wallet-btn-sm'
              onClick={() => navigate('/dashboard/wallets')}
            >
              <ArrowLeftIcon size={18} style={{ marginRight: '5px' }} /> Back
            </button>
          )}
          <h1 className={titleStyles}>USD Account Details</h1>
          {isMobile && (
            <button
              className='wallet-btn wallet-btn-sm'
              onClick={() => navigate('/dashboard/wallets')}
            >
              <ArrowLeftIcon size={18} style={{ marginRight: '5px' }} /> Back
            </button>
          )}
          <button
            onClick={handleRefresh}
            className={`wallet-btn ${isMobile ? 'wallet-btn-sm' : 'wallet-btn-default ml-auto'}`}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <RefreshCw className='animate-spin mr-1' size={16} />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className='mr-1' size={16} />
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {/* Account Overview Card */}
      <div className='px-3 py-4 sm:!px-8 sm:!py-8 bg-white rounded-[10px] shadow-sm mb-8'>
        <div className='flex items-center sm:mb-5'>
          <div className='bg-[#B3A369] rounded-full p-4 mr-4'>
            <DollarSign size={32} color='white' />
          </div>
          <div className='flex-1'>
            <h2 className='text-2xl font-semibold text-[#333333]'>
              ${(account.balance?.available || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <div className='text-gray-500'>{'Available Balance'}</div>
            {account.balance?.total !== undefined && account.balance?.total !== account.balance?.available && (
              <p className='text-sm text-gray-600 mt-1'>
                Total: ${account.balance.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
            {account.balance?.blocked && account.balance.blocked > 0 ? (
              <p className='text-sm text-red-600 mt-1 flex items-center'>
                <Clock size={14} className='mr-1' />
                ${account.balance.blocked.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} blocked (pending payments)
              </p>
            ) : null}
            {account.balance?.error && (
              <p className='text-sm text-red-500 mt-1 flex items-center'>
                <AlertCircle size={14} className='mr-1' />
                {account.balance.error}
              </p>
            )}
          </div>
          <div className='text-right'>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              account.accountStatus === 'ACTIVE'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {account.accountStatus}
            </span>
            {account.isLive && (
              <div className='mt-2 flex items-center justify-end text-xs text-green-600'>
                <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1' />
                Live Data
              </div>
            )}
          </div>
        </div>

        {/* Account Details Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-200'>
          <div>
            <label className='text-sm text-gray-500'>Account Number</label>
            <div className='flex items-center mt-1'>
              <p className='text-lg font-mono mr-2'>
                {account.accountNumber || 'Not available'}
              </p>
              {account.accountNumber && (
                <button
                  onClick={() => copyToClipboard(account.accountNumber!, 'Account number')}
                  className='text-[#B3A369] hover:text-black transition-colors'
                >
                  <CopyIcon size={16} />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className='text-sm text-gray-500'>Routing Number</label>
            <div className='flex items-center mt-1'>
              <p className='text-lg font-mono mr-2'>
                {account.routingNumber || '021508219'}
              </p>
              <button
                onClick={() => copyToClipboard(account.routingNumber || '021508219', 'Routing number')}
                className='text-[#B3A369] hover:text-black transition-colors'
              >
                <CopyIcon size={16} />
              </button>
            </div>
          </div>

          <div>
            <label className='text-sm text-gray-500'>SWIFT/BIC</label>
            <div className='flex items-center mt-1'>
              <p className='text-lg font-mono mr-2'>
                {account.swift || 'ZEITPRSJXXX'}
              </p>
              <button
                onClick={() => copyToClipboard(account.swift || 'ZEITPRSJXXX', 'SWIFT code')}
                className='text-[#B3A369] hover:text-black transition-colors'
              >
                <CopyIcon size={16} />
              </button>
            </div>
          </div>

          <div>
            <label className='text-sm text-gray-500'>Account Holder</label>
            <p className='text-lg mt-1'>
              {account.personName || account.fullNameFromOnboarding || `${account.user?.profile?.firstName} ${account.user?.profile?.lastName}`}
            </p>
          </div>

          <div>
            <label className='text-sm text-gray-500'>Bank Location</label>
            <p className='text-lg mt-1'>
              {account.bankLocation || 'Puerto Rico'}
            </p>
          </div>

          <div>
            <label className='text-sm text-gray-500'>Account Name</label>
            <p className='text-lg mt-1'>
              {account.personName || account.fullNameFromOnboarding || account.accountName}
            </p>
          </div>

          {account.lastSyncedAt && (
            <div>
              <label className='text-sm text-gray-500'>Last Updated</label>
              <p className='text-lg mt-1'>
                {new Date(account.lastSyncedAt).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className='bg-white rounded-[10px] shadow-sm mb-8'>
        <div className='border-b border-gray-200'>
          <div className='flex -mb-px'>
            <button
              className={`py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-[#B3A369] text-[#B3A369]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'deposit'
                  ? 'border-[#B3A369] text-[#B3A369]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('deposit')}
            >
              Deposit
            </button>
            <button
              className={`py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'transfer'
                  ? 'border-[#B3A369] text-[#B3A369]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('transfer')}
            >
              Transfer
            </button>
            <button
              className={`py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-[#B3A369] text-[#B3A369]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
            <button
              className={`py-4 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'usdc'
                  ? 'border-[#B3A369] text-[#B3A369]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('usdc')}
            >
              USDC ERC20
            </button>
          </div>
        </div>

        <div className='p-6'>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              <h3 className='text-lg font-semibold mb-4'>Quick Actions</h3>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <button
                  className='p-4 border border-gray-200 rounded-lg hover:border-[#B3A369] transition-colors'
                  onClick={() => setActiveTab('deposit')}
                >
                  <ArrowDownLeft className='text-[#B3A369] mb-2' size={24} />
                  <p className='font-medium'>Receive Money</p>
                  <p className='text-sm text-gray-500'>View deposit instructions</p>
                </button>

                <button
                  className='p-4 border border-gray-200 rounded-lg hover:border-[#B3A369] transition-colors'
                  onClick={() => setActiveTab('transfer')}
                >
                  <ArrowUpRight className='text-[#B3A369] mb-2' size={24} />
                  <p className='font-medium'>Send Money</p>
                  <p className='text-sm text-gray-500'>Transfer to others</p>
                </button>

                <button
                  className='p-4 border border-gray-200 rounded-lg hover:border-[#B3A369] transition-colors'
                  onClick={() => setActiveTab('history')}
                >
                  <Clock className='text-[#B3A369] mb-2' size={24} />
                  <p className='font-medium'>View History</p>
                  <p className='text-sm text-gray-500'>All transactions</p>
                </button>
              </div>

              <div className='mt-8'>
                <div className='flex justify-between items-center mb-4'>
                  <h3 className='text-lg font-semibold'>Recent Transactions</h3>
                  {transactions.length > 10 && (
                    <button
                      onClick={() => setActiveTab('history')}
                      className='text-sm text-[#B3A369] hover:underline'
                    >
                      View All ({transactions.length})
                    </button>
                  )}
                </div>
                {transactions.length > 0 ? (
                  <div className='space-y-3'>
                    {transactions
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .slice(0, 10)
                      .map((tx) => (
                      <div
                        key={tx.id}
                        className='flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors'
                        onClick={() => openTransactionDetails(tx)}
                      >
                        <div className='flex items-center'>
                          <div className={`p-2 rounded-full mr-3 ${
                            tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {tx.type === 'credit' ? (
                              <ArrowDownLeft size={16} className='text-green-600' />
                            ) : (
                              <ArrowUpRight size={16} className='text-red-600' />
                            )}
                          </div>
                          <div>
                            <p className='font-medium'>{tx.description}</p>
                            {tx.counterparty && (
                              <p className='text-xs text-gray-600'>
                                {tx.type === 'credit' ? 'From: ' : 'To: '}{tx.counterparty}
                              </p>
                            )}
                            <p className='text-xs text-gray-500 mt-1'>
                              {new Date(tx.createdAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className='text-right'>
                          <p className={`font-semibold ${
                            tx.type === 'credit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {tx.type === 'credit' ? '+' : '-'}${tx.amount.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </p>
                          <p className='text-xs text-gray-500' title={getStatusDescription(tx.status)}>
                            {getStatusDisplayName(tx.status)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='text-gray-500 text-center py-8'>No transactions yet</p>
                )}
              </div>
            </div>
          )}

          {/* Deposit Tab */}
          {activeTab === 'deposit' && (
            <div>
              <div className='flex justify-between items-center mb-4'>
                <h3 className='text-lg font-semibold'>Deposit Instructions</h3>
                {depositInstructions && (
                  <button
                    onClick={loadDepositInstructions}
                    className='flex items-center text-sm text-[#B3A369] hover:text-black'
                    disabled={loadingDepositInstructions}
                  >
                    <RefreshCw size={14} className={`mr-1 ${loadingDepositInstructions ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                )}
              </div>

              {loadingDepositInstructions && !depositInstructions ? (
                <div className='flex justify-center items-center py-12'>
                  <RefreshCw className='animate-spin text-[#B3A369]' size={32} />
                  <span className='ml-3 text-gray-600'>Loading deposit instructions...</span>
                </div>
              ) : depositInstructionsError ? (
                <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
                  <div className='flex items-start'>
                    <AlertCircle className='text-red-600 mr-2 flex-shrink-0 mt-1' size={20} />
                    <div>
                      <p className='text-sm text-red-900 font-medium'>Failed to load deposit instructions</p>
                      <p className='text-xs text-red-800 mt-1'>{depositInstructionsError}</p>
                      <button
                        onClick={loadDepositInstructions}
                        className='mt-2 text-xs text-red-700 hover:text-red-900 underline'
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              ) : depositInstructions ? (
                <>
                  <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
                    <div className='flex items-start'>
                      <Info className='text-blue-600 mr-2 flex-shrink-0 mt-1' size={20} />
                      <div>
                        <p className='text-sm text-blue-900'>
                          Send money to your account using the details below. Processing times vary by transfer type.
                        </p>
                        {depositInstructions.metadata?.source === 'database_cache' && (
                          <p className='text-xs text-blue-800 mt-2 font-medium'>
                            ⚠️ Showing cached information (live data unavailable)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='space-y-6'>
                    {/* FEDWire Section */}
                    {depositInstructions.fedwire && (
                    <div>
                      <div className='flex items-center justify-between mb-3'>
                        <h4 className='font-medium'>FEDWire Transfer (Domestic US)</h4>
                        <span className='text-xs bg-green-100 text-green-800 px-2 py-1 rounded'>
                          Same day
                        </span>
                      </div>

                        <div className='bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3'>
                          <p className='text-xs text-blue-900'>
                            <strong>FEDWire:</strong> Real-time electronic transfers via the Federal Reserve. Best for urgent, high-value transfers within the US.
                          </p>
                        </div>

                        <div className='bg-gray-50 rounded-lg p-4 space-y-3'>
                          <div className='text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide'>Beneficiary Information</div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Beneficiary Name:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.fedwire.beneficiaryName}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.fedwire!.beneficiaryName, 'Beneficiary name')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Account Number:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.fedwire.accountNumber}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.fedwire!.accountNumber, 'Account number')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Beneficiary Address:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2 text-right text-xs'>
                                {depositInstructions.fedwire.beneficiaryAddress}
                              </span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.fedwire!.beneficiaryAddress, 'Beneficiary address')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Reference field - only shown when no VRN */}
                          {depositInstructions.fedwire.reference && (
                            <div className='flex justify-between items-center'>
                              <span className='text-sm text-gray-600 font-semibold text-red-600'>Reference*:</span>
                              <div className='flex items-center'>
                                <span className='font-mono mr-2 font-semibold'>{depositInstructions.fedwire.reference}</span>
                                <button
                                  onClick={() => copyToClipboard(depositInstructions.fedwire!.reference!, 'Reference')}
                                  className='text-[#B3A369] hover:text-black'
                                >
                                  <CopyIcon size={14} />
                                </button>
                              </div>
                            </div>
                          )}

                          <div className='border-t border-gray-200 my-3'></div>
                          <div className='text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide'>Beneficiary Bank</div>

                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>ABA/Routing Number:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.fedwire.routingNumber}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.fedwire!.routingNumber, 'Routing number')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Bank Name:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.fedwire.bankName}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.fedwire!.bankName, 'Bank name')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Bank Address:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2 text-right'>{depositInstructions.fedwire.bankAddress}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.fedwire!.bankAddress, 'Bank address')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Warning for Reference field */}
                        {depositInstructions.fedwire.reference && (
                          <div className='bg-red-50 border border-red-200 rounded-lg p-3 mt-3'>
                            <p className='text-xs text-red-900'>
                              <strong>*Important:</strong> You MUST include your Zenus account number <strong>{depositInstructions.fedwire.reference}</strong> in the Reference/Memo field of your transfer. Deposits without this reference may be delayed or rejected.
                            </p>
                          </div>
                        )}
                    </div>
                    )}

                    {/* ACH Section */}
                    {depositInstructions.ach && (
                    <div>
                      <div className='flex items-center justify-between mb-3'>
                        <h4 className='font-medium'>ACH Transfer (Domestic US)</h4>
                        <span className='text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded'>
                          1-3 days
                        </span>
                      </div>

                      <div className='bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3'>
                        <div className='flex items-start'>
                          <AlertCircle className='text-amber-600 mr-2 flex-shrink-0 mt-0.5' size={16} />
                          <p className='text-xs text-amber-900'>
                            <strong>Important:</strong> ACH transfers are only available from US banks. The sender must have a US bank account.
                          </p>
                        </div>
                      </div>

                      <div className='bg-gray-50 rounded-lg p-4 space-y-3'>
                          <div className='text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide'>Beneficiary Information</div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Beneficiary Name:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.ach.beneficiaryName}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.ach!.beneficiaryName, 'Beneficiary name')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Account Number:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.ach.accountNumber}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.ach!.accountNumber, 'Account number')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Account Type:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.ach.accountType}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.ach!.accountType, 'Account type')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Beneficiary Address:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2 text-right text-xs'>
                                {depositInstructions.ach.beneficiaryAddress}
                              </span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.ach!.beneficiaryAddress, 'Beneficiary address')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Reference field - only shown when no VRN */}
                          {depositInstructions.ach.reference && (
                            <div className='flex justify-between items-center'>
                              <span className='text-sm text-gray-600 font-semibold text-red-600'>Reference*:</span>
                              <div className='flex items-center'>
                                <span className='font-mono mr-2 font-semibold'>{depositInstructions.ach.reference}</span>
                                <button
                                  onClick={() => copyToClipboard(depositInstructions.ach!.reference!, 'Reference')}
                                  className='text-[#B3A369] hover:text-black'
                                >
                                  <CopyIcon size={14} />
                                </button>
                              </div>
                            </div>
                          )}

                          <div className='border-t border-gray-200 my-3'></div>
                          <div className='text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide'>Beneficiary Bank</div>

                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>ABA/Routing Number:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.ach.routingNumber}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.ach!.routingNumber, 'Routing number')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Bank Name:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.ach.bankName}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.ach!.bankName, 'Bank name')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Bank Address:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2 text-right'>{depositInstructions.ach.bankAddress}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.ach!.bankAddress, 'Bank address')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Warning for Reference field */}
                        {depositInstructions.ach.reference && (
                          <div className='bg-red-50 border border-red-200 rounded-lg p-3 mt-3'>
                            <p className='text-xs text-red-900'>
                              <strong>*Important:</strong> You MUST include your Zenus account number <strong>{depositInstructions.ach.reference}</strong> in the Reference/Memo field of your transfer. Deposits without this reference may be delayed or rejected.
                            </p>
                          </div>
                        )}
                    </div>
                    )}

                    {/* SWIFT Section */}
                    {depositInstructions.account.swiftCode && (
                      <div>
                        <div className='flex items-center justify-between mb-3'>
                          <h4 className='font-medium'>SWIFT Transfer (International)</h4>
                          <span className='text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded'>
                            1-5 days
                          </span>
                        </div>

                        <div className='bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3'>
                          <p className='text-xs text-blue-900'>
                            <strong>SWIFT:</strong> For international transfers from banks outside the US. May include intermediary bank fees.
                          </p>
                        </div>

                        <div className='bg-gray-50 rounded-lg p-4 space-y-3'>
                          {/* Beneficiary Information */}
                          <div className='text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide'>Beneficiary</div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Beneficiary Name:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.beneficiary.name}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.beneficiary.name, 'Beneficiary name')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Account Number:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>{depositInstructions.account.accountNumber}</span>
                              <button
                                onClick={() => copyToClipboard(depositInstructions.account.accountNumber, 'Account number')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          {depositInstructions.beneficiary.address && (
                            <div className='flex justify-between items-center'>
                              <span className='text-sm text-gray-600'>Beneficiary Address:</span>
                              <div className='flex items-center'>
                                <span className='font-mono mr-2 text-right text-xs'>{depositInstructions.beneficiary.address}</span>
                                <button
                                  onClick={() => copyToClipboard(depositInstructions.beneficiary.address!, 'Beneficiary address')}
                                  className='text-[#B3A369] hover:text-black'
                                >
                                  <CopyIcon size={14} />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Beneficiary Bank (Zenus) - Swift Field 57 */}
                          <div className='border-t border-gray-200 my-3'></div>
                          <div className='text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide'>Beneficiary Bank (Swift Field 57)</div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Bank Name:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>Zenus Bank International Inc</span>
                              <button
                                onClick={() => copyToClipboard('Zenus Bank International Inc', 'Bank name')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>SWIFT Code:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>ZEITPRSJ</span>
                              <button
                                onClick={() => copyToClipboard('ZEITPRSJ', 'SWIFT code')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Bank Address:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2 text-right text-xs'>Zenus Tower, 19th Floor, 252 Ponce de Leon, San Juan, PR, 00918</span>
                              <button
                                onClick={() => copyToClipboard('Zenus Tower, 19th Floor, 252 Ponce de Leon, San Juan, PR, 00918', 'Bank address')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Intermediary Bank (JP Morgan) - Swift Field 56 */}
                          <div className='border-t border-gray-200 my-3'></div>
                          <div className='text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide'>Intermediary Bank (Swift Field 56)</div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Bank Name:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>JP Morgan Chase NA, New York, NY</span>
                              <button
                                onClick={() => copyToClipboard('JP Morgan Chase NA, New York, NY', 'Intermediary bank name')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>BIC Code:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>CHASUS33</span>
                              <button
                                onClick={() => copyToClipboard('CHASUS33', 'Intermediary BIC code')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Routing Number:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2'>021000021</span>
                              <button
                                onClick={() => copyToClipboard('021000021', 'Intermediary routing number')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                          <div className='flex justify-between items-center'>
                            <span className='text-sm text-gray-600'>Bank Address:</span>
                            <div className='flex items-center'>
                              <span className='font-mono mr-2 text-right text-xs'>New York, NY, US</span>
                              <button
                                onClick={() => copyToClipboard('New York, NY, US', 'Intermediary bank address')}
                                className='text-[#B3A369] hover:text-black'
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
                      <div className='flex items-start'>
                        <AlertCircle className='text-yellow-600 mr-2 flex-shrink-0 mt-1' size={20} />
                        <div>
                          <p className='text-sm text-yellow-900'>
                            <strong>Important:</strong> Always include your full name as it appears on your account when making deposits.
                            Deposits from third parties may be rejected.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Transfer Tab */}
          {activeTab === 'transfer' && (
            <div className='flex flex-col items-center'>
              <div className='w-full max-w-3xl'>
                <h3 className='text-lg font-semibold mb-4'>Send Money</h3>

                {/* Manage Saved Beneficiaries Section */}
                {(transferForm.transferType === 'ach' || transferForm.transferType === 'fedwire' || transferForm.transferType === 'wire') && (
                  <div className='mb-6 bg-gray-50 border border-gray-200 rounded-lg'>
                  <button
                    className='w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors rounded-lg'
                    onClick={() => setShowManageCounterparties(!showManageCounterparties)}
                  >
                    <div className='flex items-center'>
                      <Users className='mr-2 text-[#B3A369]' size={20} />
                      <span className='font-medium text-gray-900'>
                        Saved Beneficiaries ({savedCounterparties.length})
                      </span>
                    </div>
                    {showManageCounterparties ? (
                      <ChevronUp size={20} className='text-gray-600' />
                    ) : (
                      <ChevronDown size={20} className='text-gray-600' />
                    )}
                  </button>

                  {showManageCounterparties && (
                    <div className='px-4 pb-4'>
                      {savedCounterparties.length === 0 ? (
                        <p className='text-sm text-gray-500 text-center py-4'>
                          No saved beneficiaries yet. Fill the form below and click "Save Beneficiary" to add one.
                        </p>
                      ) : (
                        <div className='space-y-2'>
                          {savedCounterparties.map((cp) => (
                            <div
                              key={cp.id}
                              className='bg-white border border-gray-200 rounded-lg p-3 hover:border-[#B3A369] transition-colors'
                            >
                              <div className='flex items-start justify-between'>
                                <div className='flex-1'>
                                  <div className='font-medium text-gray-900'>{cp.nickname}</div>
                                  <div className='text-sm text-gray-600'>{cp.beneficiaryName}</div>
                                  <div className='text-xs text-gray-500 mt-1'>
                                    {cp.transferType} • {cp.accountNumber}
                                    {cp.routingNumber && ` • Routing: ${cp.routingNumber}`}
                                    {cp.swiftCode && ` • SWIFT: ${cp.swiftCode}`}
                                  </div>
                                </div>
                                <div className='flex gap-2 ml-3'>
                                  <button
                                    onClick={() => loadCounterpartyIntoForm(cp)}
                                    className='p-2 text-[#B3A369] hover:bg-[#B3A369] hover:text-white rounded transition-colors'
                                    title='Load into form'
                                  >
                                    <Download size={16} />
                                  </button>
                                  <button
                                    onClick={() => openEditCounterparty(cp)}
                                    className='p-2 text-blue-600 hover:bg-blue-600 hover:text-white rounded transition-colors'
                                    title='Edit nickname'
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => setCounterpartyToDelete(cp)}
                                    className='p-2 text-red-600 hover:bg-red-600 hover:text-white rounded transition-colors'
                                    title='Delete'
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                )}

                <div className='space-y-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Transfer Type
                    </label>
                    <select
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                      value={transferForm.transferType}
                      onChange={(e) => setTransferForm({...transferForm, transferType: e.target.value as any})}
                    >
                      <option value='internal'>Internal Transfer (Between BELOBABA accounts)</option>
                      <option value='ach'>ACH Transfer (US Banks - 1-3 days)</option>
                      <option value='fedwire'>FedWire (US Banks - Same Day)</option>
                      <option value='wire'>SWIFT International Transfer</option>
                    </select>
                  </div>

                  {transferForm.transferType === 'internal' && (
                    <div className='relative'>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        Recipient Email (BELOBABA User)
                      </label>
                      <input
                        type='email'
                        className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                        placeholder='Search by email...'
                        value={transferForm.recipientEmail}
                        onChange={(e) => {
                          setTransferForm({...transferForm, recipientEmail: e.target.value});
                          searchBelobabaUsers(e.target.value);
                        }}
                      />
                      {searchingUsers && (
                        <div className='absolute right-3 top-10'>
                          <RefreshCw className='animate-spin text-gray-400' size={16} />
                        </div>
                      )}
                      {showUserSuggestions && belobabaUsers.length > 0 && (
                        <div className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto'>
                          {belobabaUsers.map((user, index) => (
                            <button
                              key={index}
                              className='w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0'
                              onClick={() => selectBelobabaUser(user)}
                            >
                              <div className='font-medium'>{user.name}</div>
                              <div className='text-sm text-gray-500'>{user.email}</div>
                              <div className='text-xs text-gray-400'>Account: {user.accountNumber}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {(transferForm.transferType === 'ach' || transferForm.transferType === 'fedwire') && (
                    <>
                      {transferForm.transferType === 'ach' && (
                        <div className='bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4'>
                          <div className='flex items-start'>
                            <Info className='text-blue-600 mr-2 flex-shrink-0 mt-0.5' size={16} />
                            <p className='text-xs text-blue-900'>
                              ACH transfers are for US bank accounts only. Processing time: 2-3 business days. Fee: $15
                            </p>
                          </div>
                        </div>
                      )}
                      {transferForm.transferType === 'fedwire' && (
                        <div className='bg-green-50 border border-green-200 rounded-lg p-3 mb-4'>
                          <div className='flex items-start'>
                            <Info className='text-green-600 mr-2 flex-shrink-0 mt-0.5' size={16} />
                            <p className='text-xs text-green-900'>
                              <strong>FedWire - Same Day Transfer:</strong> For US bank accounts only. Funds are processed the same business day. Fee: $25
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Beneficiary Type */}
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Beneficiary Type *
                        </label>
                        <select
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          value={transferForm.personType || 'P'}
                          onChange={(e) => setTransferForm({...transferForm, personType: e.target.value as 'P' | 'L'})}
                        >
                          <option value='P'>Individual (Personal)</option>
                          <option value='L'>Corporate (Business/Company)</option>
                        </select>
                      </div>

                      {/* Name fields - conditional based on personType */}
                      {transferForm.personType === 'P' ? (
                        <div className='grid grid-cols-2 gap-2'>
                          <div>
                            <label className='block text-sm font-medium text-gray-700 mb-2'>
                              First Name *
                            </label>
                            <input
                              type='text'
                              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                              placeholder='First name'
                              value={transferForm.firstName || ''}
                              onChange={(e) => {
                                const fullName = `${e.target.value} ${transferForm.lastName || ''}`.trim();
                                setTransferForm({...transferForm, firstName: e.target.value, recipientName: fullName});
                              }}
                            />
                          </div>
                          <div>
                            <label className='block text-sm font-medium text-gray-700 mb-2'>
                              Last Name *
                            </label>
                            <input
                              type='text'
                              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                              placeholder='Last name'
                              value={transferForm.lastName || ''}
                              onChange={(e) => {
                                const fullName = `${transferForm.firstName || ''} ${e.target.value}`.trim();
                                setTransferForm({...transferForm, lastName: e.target.value, recipientName: fullName});
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Company/Business Name *
                          </label>
                          <input
                            type='text'
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='Company legal name'
                            value={transferForm.recipientName || ''}
                            onChange={(e) => setTransferForm({...transferForm, recipientName: e.target.value})}
                          />
                        </div>
                      )}

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Bank Address * <span className='text-xs text-gray-500 font-normal'>(provided by the bank)</span>
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] mb-2'
                          placeholder='Bank street address'
                          value={transferForm.beneficiaryAddress?.addressLine1}
                          onChange={(e) => setTransferForm({
                            ...transferForm,
                            beneficiaryAddress: {
                              ...transferForm.beneficiaryAddress!,
                              addressLine1: e.target.value,
                              country: 'US' // ACH requires US address
                            }
                          })}
                        />
                        <div className='grid grid-cols-2 gap-2'>
                          <input
                            type='text'
                            className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='Bank city'
                            value={transferForm.beneficiaryAddress?.city}
                            onChange={(e) => setTransferForm({
                              ...transferForm,
                              beneficiaryAddress: {
                                ...transferForm.beneficiaryAddress!,
                                city: e.target.value
                              }
                            })}
                          />
                          <select
                            className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            value={transferForm.beneficiaryAddress?.state || ''}
                            onChange={(e) => setTransferForm({
                              ...transferForm,
                              beneficiaryAddress: {
                                ...transferForm.beneficiaryAddress!,
                                state: e.target.value
                              }
                            })}
                          >
                            <option value=''>Select state</option>
                            <option value='AL'>AL - Alabama</option>
                            <option value='AK'>AK - Alaska</option>
                            <option value='AZ'>AZ - Arizona</option>
                            <option value='AR'>AR - Arkansas</option>
                            <option value='CA'>CA - California</option>
                            <option value='CO'>CO - Colorado</option>
                            <option value='CT'>CT - Connecticut</option>
                            <option value='DE'>DE - Delaware</option>
                            <option value='FL'>FL - Florida</option>
                            <option value='GA'>GA - Georgia</option>
                            <option value='HI'>HI - Hawaii</option>
                            <option value='ID'>ID - Idaho</option>
                            <option value='IL'>IL - Illinois</option>
                            <option value='IN'>IN - Indiana</option>
                            <option value='IA'>IA - Iowa</option>
                            <option value='KS'>KS - Kansas</option>
                            <option value='KY'>KY - Kentucky</option>
                            <option value='LA'>LA - Louisiana</option>
                            <option value='ME'>ME - Maine</option>
                            <option value='MD'>MD - Maryland</option>
                            <option value='MA'>MA - Massachusetts</option>
                            <option value='MI'>MI - Michigan</option>
                            <option value='MN'>MN - Minnesota</option>
                            <option value='MS'>MS - Mississippi</option>
                            <option value='MO'>MO - Missouri</option>
                            <option value='MT'>MT - Montana</option>
                            <option value='NE'>NE - Nebraska</option>
                            <option value='NV'>NV - Nevada</option>
                            <option value='NH'>NH - New Hampshire</option>
                            <option value='NJ'>NJ - New Jersey</option>
                            <option value='NM'>NM - New Mexico</option>
                            <option value='NY'>NY - New York</option>
                            <option value='NC'>NC - North Carolina</option>
                            <option value='ND'>ND - North Dakota</option>
                            <option value='OH'>OH - Ohio</option>
                            <option value='OK'>OK - Oklahoma</option>
                            <option value='OR'>OR - Oregon</option>
                            <option value='PA'>PA - Pennsylvania</option>
                            <option value='RI'>RI - Rhode Island</option>
                            <option value='SC'>SC - South Carolina</option>
                            <option value='SD'>SD - South Dakota</option>
                            <option value='TN'>TN - Tennessee</option>
                            <option value='TX'>TX - Texas</option>
                            <option value='UT'>UT - Utah</option>
                            <option value='VT'>VT - Vermont</option>
                            <option value='VA'>VA - Virginia</option>
                            <option value='WA'>WA - Washington</option>
                            <option value='WV'>WV - West Virginia</option>
                            <option value='WI'>WI - Wisconsin</option>
                            <option value='WY'>WY - Wyoming</option>
                            <option value='DC'>DC - District of Columbia</option>
                          </select>
                        </div>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] mt-2'
                          placeholder='Bank ZIP Code'
                          value={transferForm.beneficiaryAddress?.zipCode}
                          onChange={(e) => setTransferForm({
                            ...transferForm,
                            beneficiaryAddress: {
                              ...transferForm.beneficiaryAddress!,
                              zipCode: e.target.value
                            }
                          })}
                          maxLength={10}
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Bank Name *
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          placeholder='Enter recipient bank name'
                          value={transferForm.recipientBank}
                          onChange={(e) => setTransferForm({...transferForm, recipientBank: e.target.value})}
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Routing Number *
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          placeholder='Enter bank routing number (9 digits)'
                          value={transferForm.routingNumber}
                          onChange={(e) => setTransferForm({...transferForm, routingNumber: e.target.value})}
                          maxLength={9}
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Account Number *
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          placeholder='Enter account number'
                          value={transferForm.recipientAccount}
                          onChange={(e) => setTransferForm({...transferForm, recipientAccount: e.target.value})}
                          maxLength={17}
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Account Type *
                        </label>
                        <select
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          value={transferForm.accountType}
                          onChange={(e) => setTransferForm({...transferForm, accountType: e.target.value as 'CHECKING' | 'SAVINGS'})}
                        >
                          <option value='CHECKING'>Checking</option>
                          <option value='SAVINGS'>Savings</option>
                        </select>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Purpose of Payment *
                        </label>
                        <select
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          value={transferForm.purposeCode}
                          onChange={(e) => setTransferForm({...transferForm, purposeCode: e.target.value})}
                        >
                          <option value='1'>Personal / Living Expenses</option>
                          <option value='2'>Credit Card Payment</option>
                          <option value='3'>Personal Investment</option>
                          <option value='4'>Loan Repayment</option>
                          <option value='5'>Family Support</option>
                          <option value='6'>Educational / Student Support</option>
                          <option value='7'>Rent Payment</option>
                          <option value='8'>Utility Bill Payment</option>
                          <option value='9'>Charitable Donation</option>
                          <option value='10'>Purchase of Personal Goods</option>
                          <option value='11'>Gift</option>
                          <option value='12'>Insurance Premium</option>
                          <option value='13'>Medical Bills</option>
                          <option value='14'>Purchase of Property</option>
                          <option value='15'>Salary Transfer</option>
                          <option value='16'>Preferable Saving</option>
                          <option value='17'>Loan to Beneficiary</option>
                          <option value='18'>Travel Expenses</option>
                          <option value='19'>School / College Tuition Fees</option>
                          <option value='20'>Mortgage Payment</option>
                          <option value='21'>Household Repairs / Building Cost</option>
                          <option value='22'>Closure of Remitting Account</option>
                          <option value='23'>Hire of Goods</option>
                          <option value='24'>Fines / Penalties</option>
                          <option value='25'>Tax Bill</option>
                          <option value='26'>Dividends</option>
                          <option value='27'>Agricultural Goods</option>
                          <option value='28'>Real Estate Investments</option>
                          <option value='29'>Import/Export Settlements</option>
                          <option value='30'>Art Purchase Settlements</option>
                          <option value='31'>Other</option>
                        </select>
                      </div>
                    </>
                  )}

                  {transferForm.transferType === 'wire' && (
                    <>
                      <div className='bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4'>
                        <div className='flex items-start'>
                          <Info className='text-blue-600 mr-2 flex-shrink-0 mt-0.5' size={16} />
                          <p className='text-xs text-blue-900'>
                            SWIFT transfers are for international wire transfers. Processing time: 1-3 business days.
                            Additional fees may apply from intermediary banks.
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Beneficiary Type *
                        </label>
                        <select
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          value={transferForm.personType}
                          onChange={(e) => setTransferForm({...transferForm, personType: e.target.value as 'P' | 'L'})}
                        >
                          <option value='P'>Personal (Individual)</option>
                          <option value='L'>Legal Entity (Company)</option>
                        </select>
                      </div>

                      {transferForm.personType === 'P' ? (
                        <div className='grid grid-cols-2 gap-2'>
                          <div>
                            <label className='block text-sm font-medium text-gray-700 mb-2'>
                              First Name *
                            </label>
                            <input
                              type='text'
                              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                              placeholder='First name'
                              value={transferForm.recipientName?.split(' ')[0] || ''}
                              onChange={(e) => {
                                const lastName = transferForm.recipientName?.split(' ').slice(1).join(' ') || '';
                                setTransferForm({...transferForm, recipientName: `${e.target.value} ${lastName}`.trim()});
                              }}
                            />
                          </div>
                          <div>
                            <label className='block text-sm font-medium text-gray-700 mb-2'>
                              Last Name *
                            </label>
                            <input
                              type='text'
                              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                              placeholder='Last name'
                              value={transferForm.recipientName?.split(' ').slice(1).join(' ') || ''}
                              onChange={(e) => {
                                const firstName = transferForm.recipientName?.split(' ')[0] || '';
                                setTransferForm({...transferForm, recipientName: `${firstName} ${e.target.value}`.trim()});
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Company Name *
                          </label>
                          <input
                            type='text'
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='Full company name'
                            value={transferForm.recipientName}
                            onChange={(e) => setTransferForm({...transferForm, recipientName: e.target.value})}
                          />
                        </div>
                      )}

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Beneficiary Address *
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] mb-2'
                          placeholder='Street address'
                          value={transferForm.beneficiaryAddress?.addressLine1}
                          onChange={(e) => setTransferForm({
                            ...transferForm,
                            beneficiaryAddress: {
                              ...transferForm.beneficiaryAddress!,
                              addressLine1: e.target.value
                            }
                          })}
                        />
                        <div className='grid grid-cols-3 gap-2'>
                          <input
                            type='text'
                            className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='City'
                            value={transferForm.beneficiaryAddress?.city}
                            onChange={(e) => setTransferForm({
                              ...transferForm,
                              beneficiaryAddress: {
                                ...transferForm.beneficiaryAddress!,
                                city: e.target.value
                              }
                            })}
                          />
                          <input
                            type='text'
                            className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='State/Region (opt.)'
                            value={transferForm.beneficiaryAddress?.state}
                            onChange={(e) => setTransferForm({
                              ...transferForm,
                              beneficiaryAddress: {
                                ...transferForm.beneficiaryAddress!,
                                state: e.target.value
                              }
                            })}
                          />
                          <input
                            type='text'
                            className='px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='Country (ISO-2)'
                            value={transferForm.beneficiaryAddress?.country}
                            onChange={(e) => setTransferForm({
                              ...transferForm,
                              beneficiaryAddress: {
                                ...transferForm.beneficiaryAddress!,
                                country: e.target.value.toUpperCase()
                              }
                            })}
                            maxLength={2}
                          />
                        </div>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] mt-2'
                          placeholder='Postal/ZIP Code (optional)'
                          value={transferForm.beneficiaryAddress?.zipCode}
                          onChange={(e) => setTransferForm({
                            ...transferForm,
                            beneficiaryAddress: {
                              ...transferForm.beneficiaryAddress!,
                              zipCode: e.target.value
                            }
                          })}
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Account Number or IBAN *
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          placeholder='Enter account number or IBAN (e.g., DE89370400440532013000)'
                          value={transferForm.recipientAccount}
                          onChange={(e) => setTransferForm({...transferForm, recipientAccount: e.target.value.replace(/\s/g, '')})}
                          maxLength={34}
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                          You can enter either a standard account number or an IBAN
                        </p>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          SWIFT/BIC Code *
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          placeholder='Enter SWIFT/BIC code (8 or 11 characters)'
                          value={transferForm.swiftCode}
                          onChange={(e) => setTransferForm({...transferForm, swiftCode: e.target.value.toUpperCase()})}
                          maxLength={11}
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Bank Name *
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          placeholder='Enter recipient bank name'
                          value={transferForm.recipientBank}
                          onChange={(e) => setTransferForm({...transferForm, recipientBank: e.target.value})}
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Bank Address *
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          placeholder='Bank street address'
                          value={transferForm.bankAddress}
                          onChange={(e) => setTransferForm({...transferForm, bankAddress: e.target.value})}
                        />
                      </div>

                      <div className='grid grid-cols-2 gap-2'>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Bank City *
                          </label>
                          <input
                            type='text'
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='Bank city'
                            value={transferForm.bankCity}
                            onChange={(e) => setTransferForm({...transferForm, bankCity: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Bank Country *
                          </label>
                          <input
                            type='text'
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='ISO-2 Code (e.g., GB, ES)'
                            value={transferForm.bankCountry}
                            onChange={(e) => setTransferForm({...transferForm, bankCountry: e.target.value.toUpperCase()})}
                            maxLength={2}
                          />
                        </div>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Intermediary Bank (Optional)
                        </label>
                        <input
                          type='text'
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] mb-2'
                          placeholder='Intermediary bank name'
                          value={transferForm.intermediaryBank}
                          onChange={(e) => setTransferForm({...transferForm, intermediaryBank: e.target.value})}
                        />
                        <div className='grid grid-cols-2 gap-2'>
                          <input
                            type='text'
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='Intermediary SWIFT/BIC code'
                            value={transferForm.intermediarySwift}
                            onChange={(e) => setTransferForm({...transferForm, intermediarySwift: e.target.value.toUpperCase()})}
                            maxLength={11}
                          />
                          <input
                            type='text'
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                            placeholder='Country (ISO-2)'
                            value={transferForm.intermediaryBankCountry}
                            onChange={(e) => setTransferForm({...transferForm, intermediaryBankCountry: e.target.value.toUpperCase()})}
                            maxLength={2}
                          />
                        </div>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          Purpose of Payment
                        </label>
                        <select
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                          value={transferForm.purposeCode}
                          onChange={(e) => setTransferForm({...transferForm, purposeCode: e.target.value})}
                        >
                          <option value='1'>Personal / Living Expenses</option>
                          <option value='2'>Credit Card Payment</option>
                          <option value='3'>Personal Investment</option>
                          <option value='4'>Loan Repayment</option>
                          <option value='5'>Family Support</option>
                          <option value='6'>Educational / Student Support</option>
                          <option value='7'>Rent Payment</option>
                          <option value='8'>Utility Bill Payment</option>
                          <option value='9'>Charitable Donation</option>
                          <option value='10'>Purchase of Personal Goods</option>
                          <option value='11'>Gift</option>
                          <option value='12'>Insurance Premium</option>
                          <option value='13'>Medical Bills</option>
                          <option value='14'>Purchase of Property</option>
                          <option value='15'>Salary Transfer</option>
                          <option value='16'>Preferable Saving</option>
                          <option value='17'>Loan to Beneficiary</option>
                          <option value='18'>Travel Expenses</option>
                          <option value='19'>School / College Tuition Fees</option>
                          <option value='20'>Mortgage Payment</option>
                          <option value='21'>Household Repairs / Building Cost</option>
                          <option value='22'>Closure of Remitting Account</option>
                          <option value='23'>Hire of Goods</option>
                          <option value='24'>Fines / Penalties</option>
                          <option value='25'>Tax Bill</option>
                          <option value='26'>Dividends</option>
                          <option value='27'>Agricultural Goods</option>
                          <option value='28'>Real Estate Investments</option>
                          <option value='29'>Import/Export Settlements</option>
                          <option value='30'>Art Purchase Settlements</option>
                          <option value='31'>Other</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Amount (USD)
                    </label>
                    <div className='relative'>
                      <span className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500'>$</span>
                      <input
                        type='number'
                        className='w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                        placeholder='0.00'
                        value={transferForm.amount}
                        onChange={(e) => setTransferForm({...transferForm, amount: e.target.value})}
                        step='0.01'
                      />
                    </div>
                    {account.balance && (
                      <p className='text-sm text-gray-500 mt-1'>
                        Available: ${account.balance.available.toLocaleString()}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Reference <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='text'
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                      placeholder='Payment reference or description'
                      value={transferForm.reference}
                      onChange={(e) => setTransferForm({...transferForm, reference: e.target.value})}
                      required
                    />
                  </div>

                  <div className='pt-4 space-y-3'>
                    {/* Save Beneficiary Button */}
                    {(transferForm.transferType === 'ach' || transferForm.transferType === 'fedwire' || transferForm.transferType === 'wire') &&
                     transferForm.recipientAccount && transferForm.recipientName && (
                      <button
                        type='button'
                        className='w-full px-4 py-2 border border-[#B3A369] text-[#B3A369] rounded-lg hover:bg-[#B3A369] hover:text-white transition-colors duration-200'
                        onClick={() => setShowSaveCounterpartyModal(true)}
                      >
                        <Download className='inline mr-2' size={16} />
                        Save Beneficiary for Future Use
                      </button>
                    )}

                    {/* Submit Transfer Button */}
                    <button
                      className='wallet-btn wallet-btn-lg w-full'
                      onClick={handleTransfer}
                      disabled={isProcessingTransfer || !transferForm.amount || !transferForm.recipientAccount || !transferForm.reference}
                    >
                      {isProcessingTransfer ? (
                        <>
                          <RefreshCw className='animate-spin mr-2' size={20} />
                          Processing Transfer...
                        </>
                      ) : (
                        <>
                          <Send className='mr-2' size={20} />
                          Send ${transferForm.amount || '0.00'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              <div className='flex justify-between items-center mb-4'>
                <h3 className='text-lg font-semibold'>Transaction History</h3>
                <button
                  className='wallet-btn wallet-btn-sm'
                  onClick={() => {
                    // TODO: Implement export functionality
                    toast.info('Export feature coming soon');
                  }}
                >
                  <Download size={16} className='mr-1' />
                  Export
                </button>
              </div>

              {/* Filter Buttons */}
              <div className='flex gap-2 mb-4'>
                <button
                  onClick={() => setHistoryFilter('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    historyFilter === 'all'
                      ? 'bg-[#B3A369] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Transactions
                </button>
                <button
                  onClick={() => setHistoryFilter('incoming')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    historyFilter === 'incoming'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📥 Incoming
                </button>
                <button
                  onClick={() => setHistoryFilter('outgoing')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    historyFilter === 'outgoing'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📤 Outgoing
                </button>
              </div>

              {transactions.length > 0 ? (
                <div className='space-y-3'>
                  {transactions
                    .filter((tx) => {
                      if (historyFilter === 'all') return true;
                      if (historyFilter === 'incoming') return tx.type === 'credit';
                      if (historyFilter === 'outgoing') return tx.type === 'debit';
                      return true;
                    })
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((tx) => {
                    const paymentBadge = getPaymentTypeBadge(tx.paymentType);
                    const PaymentIcon = paymentBadge?.icon;

                    return (
                      <div
                        key={tx.id}
                        className='border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer'
                        onClick={() => openTransactionDetails(tx)}
                      >
                        <div className='flex items-start justify-between'>
                          <div className='flex items-start'>
                            <div className={`p-2 rounded-full mr-3 ${
                              tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                              {tx.type === 'credit' ? (
                                <ArrowDownLeft size={20} className='text-green-600' />
                              ) : (
                                <ArrowUpRight size={20} className='text-red-600' />
                              )}
                            </div>
                            <div className='flex-1'>
                              <div className='flex items-center gap-2 flex-wrap'>
                                <p className='font-medium text-gray-900'>{tx.description}</p>

                                {/* Badge de tipo de pago */}
                                {paymentBadge && PaymentIcon && (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${paymentBadge.className}`}>
                                    <PaymentIcon size={12} />
                                    {paymentBadge.label}
                                  </span>
                                )}

                                {/* Badge de dirección */}
                                {tx.direction && (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                    tx.direction === 'IN' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                                  }`}>
                                    {tx.direction === 'IN' ? '📥 In' : '📤 Out'}
                                  </span>
                                )}
                              </div>

                              {tx.counterparty && (
                                <p className='text-sm text-gray-600 mt-1'>
                                  {tx.type === 'credit' ? 'From: ' : 'To: '}{tx.counterparty}
                                </p>
                              )}
                              {tx.reference && (
                                <p className='text-sm text-gray-500 mt-1'>Ref: {tx.reference}</p>
                              )}
                              {tx.fee && tx.fee > 0 && (
                                <p className='text-sm text-orange-600 mt-1'>
                                  Fee: ${tx.fee.toFixed(2)}
                                </p>
                              )}
                              <p className='text-xs text-gray-400 mt-2'>
                                {new Date(tx.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className='text-right'>
                            <p className={`text-lg font-semibold ${
                              tx.type === 'credit' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {tx.type === 'credit' ? '+' : '-'}${tx.amount.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </p>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium mt-1 ${getStatusBadgeClass(tx.status)}`}
                              title={getStatusDescription(tx.status)}
                            >
                              {getStatusDisplayName(tx.status)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className='text-center py-12'>
                  <Clock className='mx-auto text-gray-400 mb-4' size={48} />
                  <p className='text-gray-500'>No transactions yet</p>
                  <p className='text-sm text-gray-400 mt-2'>
                    Your transaction history will appear here
                  </p>
                </div>
              )}
            </div>
          )}

          {/* USDC ERC20 Tab */}
          {activeTab === 'usdc' && (
            <div className="max-w-4xl mx-auto">
              {loadingUsdcWallet ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <RefreshCw className="h-12 w-12 animate-spin text-[#B3A369] mb-4" />
                  <p className="text-gray-600">Loading USDC wallet...</p>
                </div>
              ) : !usdcWallet?.hasWallet ? (
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 text-center">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <DollarSign className="h-10 w-10 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Enable USDC Deposits</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Generate your Ethereum (ERC20) address to receive USDC deposits directly to your Zenus account.
                  </p>
                  <button
                    onClick={handleCreateUsdcWallet}
                    disabled={creatingUsdcWallet}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {creatingUsdcWallet ? (
                      <>
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        Creating Wallet...
                      </>
                    ) : (
                      <>
                        <Zap className="h-5 w-5" />
                        Generate USDC Address
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Wallet Address Card */}
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Your USDC Address</h3>
                        <p className="text-sm text-gray-600">Ethereum (ERC20)</p>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <code className="text-sm font-mono text-gray-900 break-all">
                          {usdcWallet.address}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(usdcWallet.address);
                            toast.success('Address copied to clipboard!');
                          }}
                          className="ml-4 text-purple-600 hover:text-purple-800 transition-colors flex-shrink-0"
                        >
                          <CopyIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <a
                        href={`https://etherscan.io/address/${usdcWallet.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-white hover:bg-gray-50 text-gray-900 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 border border-gray-200"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View on Etherscan
                      </a>
                    </div>
                  </div>

                  {/* Important Information */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <Info className="h-5 w-5" />
                      Important Information
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-2">
                      <li className="flex gap-2">
                        <span>•</span>
                        <span>Only send <strong>USDC on Ethereum (ERC20)</strong> to this address</span>
                      </li>
                      <li className="flex gap-2">
                        <span>•</span>
                        <span>Deposits are automatically credited after network confirmation</span>
                      </li>
                      <li className="flex gap-2">
                        <span>•</span>
                        <span>Processing fee: <strong>0.2%</strong></span>
                      </li>
                      <li className="flex gap-2">
                        <span>•</span>
                        <span>Minimum deposit: <strong>$1.00 USDC</strong></span>
                      </li>
                    </ul>
                  </div>

                  {/* Recent USDC Deposits */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Recent USDC Deposits</h4>
                    {(() => {
                      // Filtrar transacciones USDC (excluir fees)
                      const usdcTransactions = transactions.filter(tx => {
                        const desc = (tx.description || '').toUpperCase();
                        // Solo USDC deposits (CREDIT) y excluir fees
                        return desc.includes('USDC') &&
                               !desc.includes('FEE') &&
                               tx.type === 'credit';
                      }).slice(0, 5); // Mostrar solo las 5 más recientes

                      if (usdcTransactions.length === 0) {
                        return (
                          <p className="text-sm text-gray-600 text-center py-8">
                            No USDC deposits yet. Send USDC to your address above to get started.
                          </p>
                        );
                      }

                      return (
                        <div className="space-y-3">
                          {usdcTransactions.map((tx) => (
                            <div
                              key={tx.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <ArrowDownLeft className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    +${tx.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {tx.counterparty ? `From: ${tx.counterparty.slice(0, 10)}...${tx.counterparty.slice(-6)}` : 'USDC Deposit'}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                  tx.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                  tx.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {tx.status}
                                </span>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(tx.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Transaction Details Modal - COMPACT & PROFESSIONAL */}
      {showTransactionModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header - Compact */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedTransaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {selectedTransaction.type === 'credit' ? (
                      <ArrowDownLeft size={20} className='text-green-600' />
                    ) : (
                      <ArrowUpRight size={20} className='text-red-600' />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {selectedTransaction.type === 'credit' ? '+' : '-'}${selectedTransaction.amount.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </h3>
                    <p className="text-xs text-gray-600 truncate max-w-[200px]">{selectedTransaction.description}</p>
                  </div>
                </div>
                <button
                  onClick={closeTransactionDetails}
                  className="text-gray-400 hover:text-gray-700 transition-colors p-1"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Status & Type Badges - Compact */}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${getStatusBadgeClass(selectedTransaction.status)}`}
                  title={getStatusDescription(selectedTransaction.status)}
                >
                  {getStatusDisplayName(selectedTransaction.status)}
                </span>
                {selectedTransaction.paymentType && (() => {
                  const badge = getPaymentTypeBadge(selectedTransaction.paymentType);
                  const PaymentIcon = badge?.icon;
                  return badge ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${badge.className}`}>
                      {PaymentIcon && <PaymentIcon size={12} />}
                      {badge.label}
                    </span>
                  ) : null;
                })()}
              </div>
            </div>

            {/* Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {/* Status Message - Compact */}
              {(selectedTransaction.status === 'WAITING_FOR_AML' ||
                selectedTransaction.status === 'INSERTED' ||
                selectedTransaction.status === 'PENDING' ||
                selectedTransaction.status === 'PROCESSING' ||
                selectedTransaction.status === 'SUBMITTED' ||
                selectedTransaction.status === 'AWAITING_APPROVAL') && (
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Clock className="text-blue-600 flex-shrink-0" size={16} />
                    <p className="text-xs font-medium text-blue-900">
                      {selectedTransaction.status === 'WAITING_FOR_AML' && 'Under compliance review'}
                      {selectedTransaction.status === 'INSERTED' && 'Payment created, pending processing'}
                      {selectedTransaction.status === 'PENDING' && 'Pending confirmation'}
                      {selectedTransaction.status === 'PROCESSING' && 'Processing in progress'}
                      {selectedTransaction.status === 'SUBMITTED' && 'Submitted to banking network'}
                      {selectedTransaction.status === 'AWAITING_APPROVAL' && 'Awaiting approval'}
                    </p>
                  </div>
                </div>
              )}

              {(selectedTransaction.status === 'COMPLETED' || selectedTransaction.status === 'PROCESSED') && (
                <div className="bg-green-50 border-l-4 border-green-500 rounded-r px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Check className="text-green-600 flex-shrink-0" size={16} />
                    <p className="text-xs font-medium text-green-900">Transaction completed successfully</p>
                  </div>
                </div>
              )}

              {(selectedTransaction.status === 'FAILED' || selectedTransaction.status === 'CANCELLED' || selectedTransaction.status === 'REJECTED') && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-r px-3 py-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="text-red-600 flex-shrink-0" size={16} />
                    <p className="text-xs font-medium text-red-900">
                      {selectedTransaction.status === 'CANCELLED' ? 'Transaction cancelled' : 'Transaction failed'}
                    </p>
                  </div>
                </div>
              )}

              {/* Details Grid - Compact */}
              <div className="space-y-2">
                {/* Date & Time */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-gray-400" />
                    <span className="text-xs font-medium text-gray-600">Date & Time</span>
                  </div>
                  <p className="text-xs text-gray-900 font-semibold">
                    {new Date(selectedTransaction.createdAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                {/* Transaction ID */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Info size={14} className="text-gray-400" />
                    <span className="text-xs font-medium text-gray-600">Transaction ID</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-900 font-mono">
                      {selectedTransaction.id.substring(0, 8)}...
                    </p>
                    <button
                      onClick={() => copyToClipboard(selectedTransaction.id, 'Transaction ID')}
                      className="text-[#B3A369] hover:text-black transition-colors"
                    >
                      <CopyIcon size={12} />
                    </button>
                  </div>
                </div>

                {/* Amount */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <DollarSign size={14} className="text-gray-400" />
                    <span className="text-xs font-medium text-gray-600">Amount</span>
                  </div>
                  <p className={`text-sm font-bold ${
                    selectedTransaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {selectedTransaction.type === 'credit' ? '+' : '-'}${selectedTransaction.amount.toFixed(2)}
                  </p>
                </div>

                {/* Fee */}
                {selectedTransaction.fee && selectedTransaction.fee > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Info size={14} className="text-gray-400" />
                      <span className="text-xs font-medium text-gray-600">Fee</span>
                    </div>
                    <p className="text-sm font-bold text-orange-600">
                      ${selectedTransaction.fee.toFixed(2)}
                    </p>
                  </div>
                )}

                {/* Counterparty */}
                {selectedTransaction.counterparty && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Users size={14} className="text-gray-400" />
                      <span className="text-xs font-medium text-gray-600">
                        {selectedTransaction.type === 'credit' ? 'From' : 'To'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-900 font-semibold max-w-[200px] truncate text-right">
                      {selectedTransaction.counterparty}
                    </p>
                  </div>
                )}

                {/* Reference */}
                {selectedTransaction.reference && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Info size={14} className="text-gray-400" />
                      <span className="text-xs font-medium text-gray-600">Reference</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-900 font-mono max-w-[150px] truncate">
                        {selectedTransaction.reference}
                      </p>
                      <button
                        onClick={() => copyToClipboard(selectedTransaction.reference!, 'Reference')}
                        className="text-[#B3A369] hover:text-black transition-colors"
                      >
                        <CopyIcon size={12} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Currency */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <DollarSign size={14} className="text-gray-400" />
                    <span className="text-xs font-medium text-gray-600">Currency</span>
                  </div>
                  <p className="text-xs text-gray-900 font-semibold">
                    {selectedTransaction.currency}
                  </p>
                </div>

                {/* Direction */}
                {selectedTransaction.direction && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Info size={14} className="text-gray-400" />
                      <span className="text-xs font-medium text-gray-600">Direction</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                      selectedTransaction.direction === 'IN' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                    }`}>
                      {selectedTransaction.direction === 'IN' ? '📥 Incoming' : '📤 Outgoing'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer - Compact */}
            <div className="border-t border-gray-200 px-5 py-3 bg-gray-50">
              <button
                onClick={closeTransactionDetails}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4E4A6] hover:from-[#C5A028] hover:to-[#E5D597] text-gray-900 font-semibold py-2 px-4 rounded-lg transition-all duration-200 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Counterparty Confirmation Modal */}
      {counterpartyToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto">
              <AlertCircle className="text-red-600" size={32} />
            </div>

            <h3 className="text-xl font-bold text-center text-gray-900">
              Delete Beneficiary?
            </h3>

            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
              <p className="text-gray-700"><strong>Nickname:</strong> {counterpartyToDelete.nickname}</p>
              <p className="text-gray-700"><strong>Name:</strong> {counterpartyToDelete.beneficiaryName}</p>
              <p className="text-gray-700"><strong>Account:</strong> {counterpartyToDelete.accountNumber}</p>
            </div>

            <p className="text-sm text-gray-600 text-center">
              This action cannot be undone. The beneficiary will be permanently deleted.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setCounterpartyToDelete(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCounterparty}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Counterparty Modal */}
      {showEditCounterpartyModal && counterpartyToEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-bold text-gray-900">
              Edit Beneficiary Nickname
            </h3>

            <p className="text-sm text-gray-600">
              You can only edit the nickname. Other details cannot be changed.
            </p>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Nickname *
              </label>
              <input
                type='text'
                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                placeholder='e.g., "Mom", "Supplier ABC", "Rent Payment"'
                value={counterpartyNickname}
                onChange={(e) => setCounterpartyNickname(e.target.value)}
                autoFocus
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
              <p className="text-gray-700"><strong>Beneficiary:</strong> {counterpartyToEdit.beneficiaryName}</p>
              <p className="text-gray-700"><strong>Account:</strong> {counterpartyToEdit.accountNumber}</p>
              {counterpartyToEdit.routingNumber && (
                <p className="text-gray-700"><strong>Routing:</strong> {counterpartyToEdit.routingNumber}</p>
              )}
              {counterpartyToEdit.swiftCode && (
                <p className="text-gray-700"><strong>SWIFT:</strong> {counterpartyToEdit.swiftCode}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditCounterpartyModal(false);
                  setCounterpartyToEdit(null);
                  setCounterpartyNickname('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCounterparty}
                disabled={!counterpartyNickname.trim()}
                className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#F4E4A6] hover:from-[#C5A028] hover:to-[#E5D597] text-gray-900 font-semibold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Counterparty Modal */}
      {showSaveCounterpartyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-bold text-gray-900">
              Save Beneficiary
            </h3>

            <p className="text-sm text-gray-600">
              Enter a nickname to save this beneficiary for future transfers.
            </p>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Nickname *
              </label>
              <input
                type='text'
                className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]'
                placeholder='e.g., "Mom", "Supplier ABC", "Rent Payment"'
                value={counterpartyNickname}
                onChange={(e) => setCounterpartyNickname(e.target.value)}
                autoFocus
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
              <p className="text-gray-700"><strong>Beneficiary:</strong> {transferForm.recipientName}</p>
              <p className="text-gray-700"><strong>Account:</strong> {transferForm.recipientAccount}</p>
              {(transferForm.transferType === 'ach' || transferForm.transferType === 'fedwire') && transferForm.routingNumber && (
                <p className="text-gray-700"><strong>Routing:</strong> {transferForm.routingNumber}</p>
              )}
              {transferForm.transferType === 'wire' && transferForm.swiftCode && (
                <p className="text-gray-700"><strong>SWIFT:</strong> {transferForm.swiftCode}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSaveCounterpartyModal(false);
                  setCounterpartyNickname('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCounterparty}
                disabled={!counterpartyNickname.trim()}
                className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#F4E4A6] hover:from-[#C5A028] hover:to-[#E5D597] text-gray-900 font-semibold py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {/* Modal de Confirmación */}
      {showConfirmationModal && pendingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto">
              <Info className="text-blue-600" size={32} />
            </div>

            <h3 className="text-2xl font-bold text-center text-gray-900">
              Confirm Transfer
            </h3>

            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-semibold text-gray-900">${transferForm.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="font-semibold text-gray-900">
                  {transferForm.transferType === 'ach' ? 'ACH Transfer' :
                   transferForm.transferType === 'fedwire' ? 'FedWire Transfer (Same Day)' :
                   transferForm.transferType === 'wire' ? 'SWIFT/Wire Transfer' :
                   'Internal Transfer'}
                </span>
              </div>

              {/* Fee Warning - Solo para transferencias externas */}
              {transferForm.transferType !== 'internal' && (() => {
                const fee = calculateTransferFee(transferForm.transferType);
                const amount = parseFloat(transferForm.amount || '0');
                const total = amount + fee;

                return (
                  <>
                    <div className="border-t border-gray-300 my-2"></div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Transfer Fee:</span>
                      <span className="font-semibold text-orange-600">${fee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                      <span className="text-gray-900 font-bold">Total to be Blocked:</span>
                      <span className="font-bold text-lg text-blue-600">${total.toFixed(2)}</span>
                    </div>
                  </>
                );
              })()}

              <div className="border-t border-gray-300 my-2"></div>

              <div className="flex justify-between">
                <span className="text-gray-600">Recipient:</span>
                <span className="font-semibold text-gray-900">{transferForm.recipientName}</span>
              </div>
              {transferForm.recipientAccount && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Account:</span>
                  <span className="font-semibold text-gray-900">{transferForm.recipientAccount}</span>
                </div>
              )}
              {transferForm.reference && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Reference:</span>
                  <span className="font-semibold text-gray-900">{transferForm.reference}</span>
                </div>
              )}
            </div>

            {/* Warning adicional para transferencias externas */}
            {transferForm.transferType !== 'internal' && (() => {
              const fee = calculateTransferFee(transferForm.transferType);
              const amount = parseFloat(transferForm.amount || '0');
              const total = amount + fee;

              return (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                  <div className="flex items-start">
                    <AlertCircle className="text-amber-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-900 mb-1">Important: Fee Information</p>
                      <p className="text-amber-800">
                        A <strong>${fee.toFixed(2)} fee</strong> will be applied to this transfer.
                        The total amount of <strong>${total.toFixed(2)}</strong> (${amount.toFixed(2)} + ${fee.toFixed(2)} fee)
                        will be reserved from your account until the transfer is completed.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <p className="text-sm text-gray-600 text-center">
              Please confirm that all details are correct before proceeding.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelTransfer}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTransfer}
                className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#F4E4A6] hover:from-[#C5A028] hover:to-[#E5D597] text-gray-900 font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Éxito */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto">
              <Check className="text-green-600" size={32} />
            </div>

            <h3 className="text-2xl font-bold text-center text-gray-900">
              Transfer Successful!
            </h3>

            <div className="space-y-2">
              {successMessage.split('\n').map((line, index) => (
                line.trim() && (
                  <p key={index} className="text-center text-gray-600">
                    {line}
                  </p>
                )
              ))}

              {newBalance !== null && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg">
                  <p className="text-center text-gray-600">New Balance:</p>
                  <p className="text-2xl font-bold text-center text-green-600">
                    ${newBalance.toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={handleSuccessConfirm}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4E4A6] hover:from-[#C5A028] hover:to-[#E5D597] text-gray-900 font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Processing Transfer Overlay - BLOQUEA TODA LA PANTALLA */}
      {isProcessingTransfer && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-8 space-y-6 max-w-md w-full mx-4">
            <div className="flex flex-col items-center">
              <RefreshCw className="animate-spin text-[#B3A369]" size={64} />
              <h3 className="text-2xl font-bold text-gray-900 mt-6">
                Processing Transfer...
              </h3>
              <p className="text-gray-600 text-center mt-3">
                Please wait while we process your transfer. Do not close this window or navigate away.
              </p>
              <div className="mt-6 text-sm text-gray-500 text-center">
                This may take a few moments
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}